/**
 * Strings for the clinical and business modules.
 *
 * Kept beside the core dictionary rather than inside it so each file stays
 * readable. The same rule applies: Arabic is a PEER of English, typed against
 * it, so a missing key is a compile error rather than an English sentence
 * appearing in the middle of an Arabic screen.
 */

const en = {
  zones: {
    group: {
      upper_face: 'Upper face',
      eyes: 'Eyes',
      mid_face: 'Mid face',
      lips: 'Lips & mouth',
      lower_face: 'Lower face',
      neck: 'Neck',
      trunk: 'Trunk',
      arms: 'Arms',
      legs: 'Legs',
    },
    zoneList: 'Zones',
    hoverHint: 'Point at the figure or choose a zone from the list.',
    recorded: 'recorded',
    title: 'Face & body chart',
    subtitle: 'Select a zone to record a finding, a procedure with its dose, or a photograph.',
    region: 'Region',
    view: 'View',
    face: 'Face',
    body: 'Body',
    front: 'Front',
    back: 'Back',
    sideR: 'R',
    sideL: 'L',
    viewNote: 'Seen from the front: the patient\u2019s right is on your left.',
    viewNoteBack: 'Seen from behind: the patient\u2019s right is on your right.',
    selectZone: 'Select a zone',
    selectZoneHint: 'Click an area of the figure. Findings and procedures are recorded on it.',
    currentFinding: 'Current finding',
    noFinding: 'Nothing recorded on this zone.',
    procedures: 'Procedures done here',
    noProcedures: 'No procedures recorded on this zone.',
    planned: 'Planned here',
    photos: 'Photographs of this zone',
    noPhotos: 'No photographs linked to this zone yet.',
    recordFinding: 'Record finding',
    addProcedure: 'Add procedure',
    addPhoto: 'Add photograph',
    findingLabel: 'Finding',
    severityLabel: 'Severity',
    dose: 'Dose',
    doseHint: 'As given: 20 units, 1.5 ml, 400 shots.',
    doseInvalid: 'Enter the dose as a number, for example 20 or 1.5.',
    doseUnit: 'Unit',
    product: 'Product',
    productHint: 'Brand and concentration, as on the vial.',
    lot: 'Lot',
    lotHint: 'The lot number, for traceability.',
    noProcedureItems: 'No derma or aesthetic procedures in the catalogue yet. Add the standard procedures under Clinic, Treatment catalogue.',
    willMark: 'Completing this marks {zone} with {mark} on the chart.',
    legendFinding: 'Finding recorded',
    legendProcedure: 'Procedures done (count)',
    zone: {
      scalp: 'Scalp', forehead: 'Forehead', glabella: 'Glabella', temple_r: 'Right temple', temple_l: 'Left temple',
      brow_r: 'Right brow', brow_l: 'Left brow', periorbital_r: 'Right periorbital area', periorbital_l: 'Left periorbital area',
      tear_trough_r: 'Right tear trough', tear_trough_l: 'Left tear trough', nose: 'Nose', cheek_r: 'Right cheek', cheek_l: 'Left cheek',
      nasolabial_r: 'Right nasolabial fold', nasolabial_l: 'Left nasolabial fold', upper_lip: 'Upper lip', lower_lip: 'Lower lip',
      perioral: 'Perioral area', marionette_r: 'Right marionette line', marionette_l: 'Left marionette line', chin: 'Chin',
      jawline_r: 'Right jawline', jawline_l: 'Left jawline', masseter_r: 'Right masseter', masseter_l: 'Left masseter', neck: 'Neck',
      decolletage: 'D\u00e9colletage', shoulder_r: 'Right shoulder', shoulder_l: 'Left shoulder', chest: 'Chest', abdomen: 'Abdomen',
      flank_r: 'Right flank', flank_l: 'Left flank', back_upper: 'Upper back', back_lower: 'Lower back',
      arm_upper_r: 'Right upper arm', arm_upper_l: 'Left upper arm', forearm_r: 'Right forearm', forearm_l: 'Left forearm',
      hand_r: 'Right hand', hand_l: 'Left hand', underarm_r: 'Right underarm', underarm_l: 'Left underarm', bikini: 'Bikini line',
      buttock_r: 'Right buttock', buttock_l: 'Left buttock', thigh_r: 'Right thigh', thigh_l: 'Left thigh', knee_r: 'Right knee', knee_l: 'Left knee',
      lower_leg_r: 'Right lower leg', lower_leg_l: 'Left lower leg', foot_r: 'Right foot', foot_l: 'Left foot',
    },
    finding: {
      normal: 'Normal', wrinkles_dynamic: 'Dynamic wrinkles', wrinkles_static: 'Static wrinkles', volume_loss: 'Volume loss', laxity: 'Laxity',
      asymmetry: 'Asymmetry', pigmentation: 'Pigmentation', melasma: 'Melasma', acne: 'Acne', acne_scars: 'Acne scars', scar: 'Scar',
      lesion: 'Lesion', mole: 'Mole', redness: 'Redness', rosacea: 'Rosacea', enlarged_pores: 'Enlarged pores', unwanted_hair: 'Unwanted hair',
      hair_loss: 'Hair loss', cellulite: 'Cellulite', fat_deposit: 'Fat deposit', stretch_marks: 'Stretch marks', tattoo: 'Tattoo', other: 'Other',
    },
    mark: {
      toxin: 'Botulinum toxin', filler: 'Filler', booster: 'Skin booster', prp: 'PRP', mesotherapy: 'Mesotherapy', thread: 'Threads',
      laser: 'Laser', ipl: 'IPL', peel: 'Chemical peel', microneedling: 'Microneedling', rf: 'Radiofrequency', hifu: 'HIFU',
      cryolipolysis: 'Cryolipolysis', excision: 'Excision', cryotherapy: 'Cryotherapy', electrocautery: 'Electrocautery', biopsy: 'Biopsy',
      injection_other: 'Injection', other: 'Other procedure',
    },
    unit: { units: 'units', ml: 'ml', mg: 'mg', sessions: 'sessions', shots: 'shots', threads: 'threads', cm2: 'cm\u00b2', vials: 'vials', minutes: 'minutes' },
    severity: { mild: 'Mild', moderate: 'Moderate', severe: 'Severe' },
  },
  chart: {
    title: 'Dental chart',
    subtitle: 'Select a tooth, then one or more surfaces, to record findings, treatments, notes or images.',
    tooth: 'Tooth',
    dentition: 'Dentition',
    dentitionKind: { permanent: 'Permanent', primary: 'Primary', mixed: 'Mixed' },
    charted: 'charted',
    selectTooth: 'Select a tooth',
    selectToothHint: 'Click a tooth in the chart. Click again on its surfaces to select them.',
    selectSurfacesFirst: 'Select at least one surface first.',
    surfacesSelected: 'surfaces selected',
    currentStatus: 'Current status',
    recordFinding: 'Record finding',
    recordSurface: 'Record surface finding',
    addTreatment: 'Add treatment',
    addNote: 'Add note',
    capture: 'Capture image',
    save: 'Save',
    media: 'Images for this tooth',
    noMedia: 'No images linked to this tooth yet.',
    sharedFileNote: 'These are the same stored files as in the patient imaging library, not copies.',
    treatments: 'Treatments',
    noTreatments: 'No treatments recorded on this tooth.',
    simulated: 'Simulated',
    procedure: 'Procedure',
    price: 'Price for this patient',
    removeTreatment: 'Remove treatment',
    removeTreatmentBody:
      'For a treatment recorded by mistake. If it had marked the tooth on the chart, the chart is put back to what it was — as a new entry, so the history keeps both the mistake and its correction. Not possible once it is on an invoice.',
    completedIsFixed: 'The procedure and the tooth are fixed once the work is completed, because the chart was written from them. Remove it and record the right one if those are wrong.',
    priceAfterBilled: 'Already invoiced: the invoice keeps the price it was charged at. This changes the record only.',
    chargeable: 'Charge the patient for this',
    chargeableHint:
      'On by default. Clear it for a warranty redo, a courtesy, or work recorded for the record only — it still counts in the statistics, but it can never be put on an invoice.',
    priceHint: 'Starts from the catalogue price. Change it for a discount or a quoted rate.',
    cataloguePrice: 'Catalogue price',
    priceInvalid: 'Enter a price as a number, for example 67.50.',
    performedOn: 'Performed on',
    performedOnHint: 'Defaults to today. Change it if you are charting work done earlier.',
    willChart: 'Completing this marks tooth {tooth} as {status} on the chart.',
    markCompleted: 'Mark completed',
    captureHint: 'Images captured on a tooth appear here and in that tooth’s own gallery — one file, not two copies.',
    simulatorWarning:
      'This is the labelled acquisition simulator. It produces a synthetic image, not a real radiograph, and must never be used for diagnosis.',
    confirmPatient: 'I confirm this is the correct patient and tooth.',
    status: {
      sound: 'Sound',
      caries: 'Caries',
      restored: 'Restored',
      crowned: 'Crown',
      implant: 'Implant',
      root_canal: 'Root canal',
      missing: 'Missing',
      unerupted: 'Unerupted',
      retained_primary: 'Retained primary',
      extracted: 'Extracted',
      bridge_pontic: 'Bridge pontic',
      veneer: 'Veneer',
      fractured: 'Fractured',
      watch: 'Watch',
    },
    surface: {
      mesial: 'Mesial',
      distal: 'Distal',
      buccal: 'Buccal',
      labial: 'Labial',
      palatal: 'Palatal',
      lingual: 'Lingual',
      occlusal: 'Occlusal',
      incisal: 'Incisal',
    },
    finding: {
      sound: 'Sound',
      caries: 'Caries',
      restoration: 'Restoration',
      sealant: 'Sealant',
      fracture: 'Fracture',
      wear: 'Wear',
      discolouration: 'Discolouration',
      watch: 'Watch',
    },
  },
  treatmentStatus: {
    planned: 'Planned',
    accepted: 'Accepted',
    in_progress: 'In progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
    amended: 'Amended',
  },
  finance: {
    title: 'Finance',
    invoices: 'Invoices',
    payments: 'Payments',
    expenses: 'Expenses',
    reports: 'Reports',
    newInvoice: 'New invoice',
    recordPayment: 'Record payment',
    recordExpense: 'Record expense',
    number: 'Number',
    patient: 'Patient',
    issued: 'Issued',
    due: 'Due',
    total: 'Total',
    paid: 'Paid',
    outstanding: 'Outstanding',
    status: 'Status',
    draft: 'Draft',
    posted: 'Posted',
    expenseCategory: { rent: 'Rent', utilities: 'Utilities', salaries: 'Salaries', supplies: 'Supplies', maintenance: 'Maintenance', marketing: 'Marketing', other: 'Other' } as Record<string, string>,
    method: 'Method',
    amount: 'Amount',
    reference: 'Reference',
    category: 'Category',
    description: 'Description',
    post: 'Post invoice',
    postHint: 'Posting allocates the number and freezes the lines. Corrections then need a credit note.',
    billed: 'Billed',
    billedHint: 'Value of posted invoices in the period',
    collected: 'Collected',
    collectedHint: 'Money actually received',
    outstandingHint: 'Posted and unpaid',
    expensesHint: 'Recorded operating costs',
    labCost: 'Laboratory cost',
    commission: 'Dentist commission',
    profitEstimate: 'Estimated profit',
    profitHint:
      'Collected minus recorded expenses, laboratory cost and commission. An estimate, not accrual profit.',
    aging: 'Receivables aging',
    current: 'Not yet due',
    byDentist: 'By dentist',
    byCategory: 'By category',
    monthly: 'Monthly collections',
    uniquePatients: 'Patients',
    uniqueTeeth: 'Teeth',
    procedures: 'Procedures',
    produced: 'Produced',
    noInvoices: 'No invoices yet.',
    noPayments: 'No payments recorded.',
    noExpenses: 'No expenses recorded.',
    selectTreatments: 'Unbilled completed treatments',
    noUnbilled: 'No unbilled treatments for this patient.',
    alreadyBilled: 'Already billed',
    notChargeable: 'Not chargeable',
    notChargeableWhy:
      'Recorded as work the patient is not charged for here — historical activity, a warranty redo, or a courtesy. Reopen the treatment to make it chargeable.',
    hiddenNotChargeable: '{n} completed treatment(s) are marked not chargeable and cannot be invoiced.',
    showNotChargeable: 'Show them',
    hideNotChargeable: 'Hide them',
    installments: 'Split into installments',
    none: 'None',
    payFull: 'Pay in full',
    invoiceNumber: 'Invoice',
    settleThis: 'Record payment',
    settleAll: 'Settle the balance',
    creditOnAccount: 'Credit on account',
    creditOnAccountHint: 'Paid ahead; applied to the next invoice posted.',
    searchPayments: 'Search',
    searchPaymentsHint: 'Name, reference, category…',
    direction: 'In / out',
    inAndOut: 'In and out',
    moneyIn: 'In',
    moneyOut: 'Out',
    showReversed: 'Show reversed payments and their reversals',
    ownerDrawFor: 'Owner draw — {name}',
    unbilled: 'Unbilled work',
    unbilledHint:
      'Completed, chargeable, and never put on an invoice. Nothing on the money side knows about these until they are.',
    noUnbilled2: 'Everything that has been done has been invoiced.',
    invoiceNow: 'Invoice now',
    payables: 'Money owed',
    payablesHint:
      'What the clinic owes suppliers and laboratories, from posted bills and received lab work — the same facts the ledger was posted from.',
    nothingOwed: 'Nothing is owed to anyone.',
    owedToSuppliers: 'Owed to suppliers',
    owedToLabs: 'Owed to laboratories',
    pendingAtLab: 'At the lab, not yet owed',
    openBills: '{n} bill(s)',
    pay: 'Pay',
    paySupplier: 'Pay supplier',
    payLaboratory: 'Pay laboratory',
    payingTo: 'Paying {name}. Outstanding: {amount}.',
    paidOut: 'Where the money went',
    paidOutHint: 'Money out in the period, by who received it. Reversed payments are excluded from both sides.',
    toDentists: 'To dentists',
    toSuppliers: 'To suppliers',
    toLaboratories: 'To laboratories',
    refunds: 'Refunds to patients',
    otherOut: 'Other',
    expensesByCategory: 'Expenses by category',
    counterparty: 'To / from',
    settlementFor: 'Dentist settlement — {name}',
    supplierPayment: 'Supplier — {name}',
    laboratoryPayment: 'Laboratory — {name}',
    settlingInvoice: 'Paying invoice {number}. Anything over its balance goes to this patient’s other open invoices.',
    receivedOn: 'Received on',
    receivedOnHint: 'Defaults to today. Change it if the money came in earlier.',
    onHold: 'on hold',
    cardOnHold: 'Card payments are agreed but not switched on yet, so only cash can be taken today.',
    methods: {
      cash: 'Cash',
      card: 'Card',
      transfer: 'Bank transfer',
      cheque: 'Cheque',
      other: 'Other',
    },
  },
  remove: {
    confirm: 'Remove',
    delete: 'Delete',
    edit: 'Edit',
    archive: 'Archive',
    restore: 'Restore',
    typeToConfirm: 'Type {word} to confirm.',
    wasDeleted: 'Deleted.',
    wasDeactivated: 'Kept, and marked inactive — it stays out of the lists but its history still reads correctly.',
    keptBecause: 'It could not be deleted because it is referenced by',
    cannotUndo: 'This cannot be undone.',
    deletePatient: 'Delete patient',
    deletePatientBody:
      'Only for a record created by mistake. A patient with any history cannot be deleted — archive them instead, which keeps everything and takes them out of the working list.',
    archivePatient: 'Archive patient',
    archivePatientBody:
      'They come out of the patient list and keep every record. You can bring them back at any time.',
    restorePatient: 'Restore patient',
    deleteInvoice: 'Discard draft invoice',
    deleteInvoiceBody:
      'A draft has no number and has touched no accounts, so nothing is lost. Any treatments on it go back to being unbilled.',
    postedCannotDelete:
      'Posted invoices cannot be deleted — the gap in the numbering is what an audit looks for. Issue a credit note instead.',
    voidPayment: 'Reverse payment',
    voidPaymentBody:
      'The payment stays on the record and an opposing entry is written beside it. The invoices it paid go back to being unpaid.',
    voidReason: 'Why is this being reversed?',
    voidReasonHint: 'Kept with the reversal. Say enough that someone reading the ledger next year understands it.',
    commissionWarning:
      'This payment earned {amount} of commission that has already been paid out. Reversing it leaves that owing back, and it will come off the next settlement.',
    deleteSupplier: 'Remove supplier',
    deleteLaboratory: 'Remove laboratory',
    deleteMaterial: 'Remove item',
    deletePurchase: 'Discard purchase',
    deleteLabOrder: 'Discard lab order',
    deleteOrderBody: 'Only possible while it is still a draft. Once it has been sent, cancel it instead.',
    editSupplier: 'Edit supplier',
    editLaboratory: 'Edit laboratory',
    editMaterial: 'Edit item',
    retired: 'Retired',
    reactivate: 'Bring back',
    reversal: 'Reversal',
    reversed: 'Reversed',
  },
  transfer: {
    title: 'Import and export',
    exportTitle: 'Export to Excel',
    exportHint: 'Every export is a real .xlsx file: amounts are numbers you can total, dates are dates.',
    importTitle: 'Import patients',
    importHint: 'Bring patients in from whatever you used before.',
    download: 'Download',
    downloading: 'Preparing…',
    downloaded: '{name} — {rows} row(s)',
    needsPermission: 'You do not have permission for this one.',
    chooseDentist: 'Dentist',
    from: 'From',
    to: 'To',
    wholeHistory: 'Leave the dates blank for everything.',
    getTemplate: 'Download the template',
    templateHint:
      'Fill one row per patient and upload it below. Your own spreadsheet works too — common column names are recognised.',
    chooseFile: 'Choose a file',
    checking: 'Checking…',
    checked: '{total} row(s): {ready} ready, {duplicates} possible duplicate(s), {errors} with problems.',
    importReady: 'Import {n} patient(s)',
    importing: 'Importing…',
    imported: 'Imported {n} patient(s).',
    nothingToImport: 'Nothing in this file can be imported yet.',
    line: 'Row',
    rowStatus: 'Status',
    rowName: 'Name',
    rowProblem: 'Notes',
    statusReady: 'Will be added',
    statusDuplicate: 'Possible duplicate',
    statusError: 'Cannot be read',
    looksLike: 'Looks like {name} ({number})',
    includeDuplicates: 'Add the possible duplicates too',
    includeDuplicatesHint:
      'Off by default. Families share phone numbers and a father and son share a name, so these are yours to judge.',
    exportKinds: {
      patients: 'Patients',
      'patients-full': 'Patients — everything, one row each',
      'patients-by-dentist': 'Patients by dentist',
      treatments: 'Procedures',
      invoices: 'Invoices',
      payments: 'Payments',
      inventory: 'Inventory',
      appointments: 'Appointments',
    },
  },
  rx: {
    title: 'Prescriptions',
    new: 'New prescription',
    state: 'State',
    items: 'Medicines',
    addItem: 'Add medicine',
    medicine: 'Medicine',
    dose: 'Dose',
    route: 'Route',
    frequency: 'Frequency',
    duration: 'Duration',
    instructions: 'Instructions',
    quantity: 'Quantity',
    check: 'Run safety check',
    checking: 'Checking',
    issue: 'Issue prescription',
    cancelRx: 'Cancel prescription',
    template: 'Template',
    useTemplate: 'Use a template',
    templateNote: 'Selecting a template always triggers a fresh check for this patient.',
    states: {
      draft: 'Draft',
      blocked: 'Blocked',
      checked: 'Checked',
      issued: 'Issued',
      cancelled: 'Cancelled',
    },
    findings: 'Safety findings',
    noFindings: 'No findings were reported.',
    blockedNotice: 'Issuing is blocked. Review the findings and edit the draft.',
    staleNotice: 'The record changed since the last check. Run the check again before issuing.',
    syntheticNotice:
      'Safety checking is running on NON-CLINICAL test data. It must not be relied on for patient care.',
    notGuarantee:
      'A completed check is decision support and requires prescriber review. It is not a guarantee of safety.',
    noPrescriptions: 'No prescriptions yet.',
    printDraft: 'Draft preview',
    none: 'No prescriptions for this patient.',
    saveDraft: 'Save draft',
    edit: 'Edit',
    discard: 'Discard',
    removeItem: 'Remove',
    stale: 'Needs re-checking',
    unresolved: 'Not in the dataset',
    mine: 'Mine',
    clinicWide: 'Whole clinic',
    templateHint: 'Fills the medicines in. The prescription is still checked against this patient.',
    saveAsTemplate: 'Save these medicines as a template',
    templates: 'Templates',
    newTemplate: 'New template',
    editTemplate: 'Edit template',
    templateName: 'Template name',
    templateScope: 'Who can use it',
    personalScope: 'Only me',
    clinicScope: 'Everyone in the clinic',
    clinicScopeNeeds: 'Publishing to the whole clinic needs the template-management permission; a personal one needs nothing.',
    templateDescription: 'Description',
    noTemplates: 'No templates yet. Save one from a prescription, or write one here.',
    archiveTemplate: 'Remove template',
    scopeBadge: { personal: 'Mine', clinic: 'Clinic' },
    saveAsTemplateHint: 'Leave empty not to save one. Give it a name to reuse it later.',
    blockedTitle: 'This prescription cannot be issued.',
    blockedHelp:
      'Edit the medicines and check again, or discard it. It stays in the record either way — nothing is deleted.',
    editVoidsCheck:
      'Editing discards the previous safety check. These medicines are not the ones it examined, so it has to run again.',
    syntheticNote:
      'This verdict came from NON-CLINICAL test data and must not be relied on for patient care.',
    printValid: 'Print prescription',
  },
  lab: {
    title: 'Laboratory',
    orders: 'Orders',
    openOrders: 'Lab work due',
    noneForPatient: 'No laboratory work for this patient.',
    raiseFromChart: 'Plan a crown, denture or other lab item on the chart, then raise the order from Laboratory.',
    patientCostNote:
      'The clinic’s cost to the laboratory is separate from what the patient is charged. They are never the same line.',
    laboratories: 'Laboratories',
    newOrder: 'New lab order',
    newLab: 'Add laboratory',
    item: 'Item',
    teeth: 'Teeth',
    shade: 'Shade',
    due: 'Due',
    labCost: 'Lab cost',
    patientCharge: 'Patient charge',
    editOrder: 'Edit order',
    correctStatus: 'Correct the status',
    correctStatusHint:
      'For a wrong press — "collected" when it was not, or "received" a day early. Goes anywhere; the reason is kept in the order\'s history, and what the clinic owes the lab follows.',
    correctionReason: 'Why?',
    payableRecorded: 'Owed to lab',
    pendingAtLab: 'At the lab',
    labCostEditNote: 'Changing the cost after the work has arrived changes what the clinic owes the laboratory; the books follow.',
    costNote:
      'The clinic cost and the patient charge are separate. A lab bill never duplicates the treatment charge.',
    advance: 'Advance',
    whatsapp: 'WhatsApp draft',
    remind: 'Send reminder',
    overdue: 'Overdue',
    outstanding: 'Outstanding',
    noOrders: 'No laboratory orders.',
    noLabs: 'No laboratories yet.',
    states: {
      draft: 'Draft',
      awaiting_pickup: 'Awaiting pickup',
      collected: 'Collected',
      in_preparation: 'In preparation',
      ready: 'Ready',
      received: 'Received',
      fitting: 'Fitting',
      adjustment_requested: 'Adjustment requested',
      returned_for_adjustment: 'Returned for adjustment',
      completed: 'Completed',
      cancelled: 'Cancelled',
    },
  },
  inventory: {
    title: 'Inventory',
    materials: 'Materials',
    suppliers: 'Suppliers',
    purchases: 'Purchases',
    alerts: 'Alerts',
    sku: 'SKU',
    name: 'Name',
    onHand: 'On hand',
    threshold: 'Reorder at',
    location: 'Location',
    value: 'Value',
    unitCost: 'Unit cost',
    newMaterial: 'Add material',
    newSupplier: 'Add supplier',
    askPriceTitle: 'Ask for a price',
    askWhichSupplier: 'Which supplier?',
    askQuantity: 'How many packs?',
    newItemInline: '+ New item',
    postBill: 'Post the bill',
    postBillHint: 'Records what is owed to the supplier and the price history. Stock is unaffected.',
    receiveGoods: 'Receive the goods',
    receiveGoodsHint: 'Puts the items into stock. The supplier balance is unaffected.',
    notPosted: 'Not posted',
    outstandingWith: 'Outstanding with {name}',
    contact: 'Contact person',
    address: 'Address',
    newPurchase: 'Record purchase',
    consume: 'Record use',
    askPrice: 'Ask for price',
    level: { ok: 'In stock', low: 'Low', out: 'Out of stock', negative: 'Negative' },
    negativeNote: 'Negative stock means the records disagree with reality. Reconcile with a count.',
    packs: 'Packs',
    packSize: 'Units per pack',
    supplier: 'Supplier',
    invoiceNumber: 'Supplier invoice number',
    action: 'Action',
    actions: {
      post_bill: 'Post the bill only',
      confirm_receipt: 'Confirm goods received only',
      post_and_receive: 'Post and receive',
    },
    actionHint:
      'Posting a bill creates the payable. Receiving goods creates stock. They are separate events.',
    received: 'Received',
    billedOnly: 'Billed, not received',
    noMaterials: 'No materials yet.',
    noSuppliers: 'No suppliers yet.',
    noPurchases: 'No purchases recorded.',
    costingMethod: 'Costing method',
    lowStock: 'Low or out of stock',
    expiring: 'Expiring soon',
    allGood: 'Nothing needs attention.',
    quantity: 'Quantity',
    search: 'Search by name, code or location',
    allCategories: 'All categories',
    newCategory: 'New category',
    noMatches: 'Nothing matches those filters.',
    usedOn: 'Used on',
    usedOnHint: 'Defaults to now. Change it if you are recording something from earlier.',
    newItem: 'New item',
    packSizeHint: 'Base units in one pack — a box of 100 gloves is 100.',
    newItemHint:
      'Adds the item to the catalogue with no stock. Stock arrives through a goods receipt, so adding an item can never invent quantity.',
    itemName: 'Item name',
    baseUnit: 'Base unit',
    packUnit: 'Pack unit',
    reorderLevel: 'Reorder level',
    category: 'Category',
    created: 'Item added.',
  },
  dentists: {
    title: 'Dentists',
    statement: 'Statement',
    assignedPatients: 'Assigned patients',
    completedProcedures: 'Completed procedures',
    commission: 'Commission',
    accrued: 'Accrued',
    settled: 'Settled',
    unsettled: 'Unsettled',
    basis: 'Basis',
    rate: 'Rate',
    agreements: 'Agreements',
    newAgreement: 'New agreement',
    settle: 'Settle commission',
    effectiveFrom: 'Effective from',
    deductLab: 'Deduct laboratory cost',
    basisKind: {
      collected: 'On collections',
      billed: 'On billed',
      net_of_lab: 'Net of lab',
      salary: 'Monthly salary',
      profit: 'Owner — the clinic\'s profit',
    },
    basisExplainer: {
      collected: 'Paid on money the clinic has actually COLLECTED. An invoice the patient never pays earns the dentist nothing, so there is nothing to claw back.',
      billed: 'Paid on what was BILLED, whether or not the patient has paid. The clinic carries the risk if they never do.',
      net_of_lab: 'Paid on collections after the laboratory cost of the work is deducted.',
      salary: 'A fixed monthly amount, independent of what was produced or collected.',
      profit: 'The owner. Nothing accrues per procedure: what is left after every other dentist, supplier, laboratory and expense has been paid is theirs, and they draw on it as they choose.',
    },
    whoCarriesRisk: 'Who carries the risk if the patient does not pay',
    riskClinic: 'The clinic',
    riskShared: 'Shared with the dentist',
    salaryPayNote:
      'A salaried dentist is paid for the month, not for particular procedures. The production figures below are there to be looked at, not to be paid against.',
    payAgainstSalary: 'Pay against salary',
    salaryOwed: 'Salary owed',
    commissionOwed: 'Commission owed',
    payKind: 'How this dentist is paid',
    commissionOnly: 'Commission',
    salaryOnly: 'Salary',
    salaryAndCommission: 'Salary and commission',
    monthlySalary: 'Monthly salary',
    salary: 'Salary',
    salaryNote:
      'A salary and a commission are separate agreements and run side by side. Changing one never ends the other.',
    postSalary: 'Post salary',
    unpostedSalary: 'Salary not yet posted',
    unpostedSalaryNote:
      'Salary becomes owed when the month is done, and posting it is a deliberate act — opening this page does not create it.',
    proRated: 'pro-rated over {days} of {of} days',
    payDentist: 'Pay dentist',
    payAmount: 'Amount to pay',
    payFull: 'Pay everything outstanding',
    payPart: 'Pay a specific amount',
    payNote:
      'You name the amount. It pays off the oldest earnings first, and an earning that is only part-paid still shows what is left on it.',
    payMethod: 'Method',
    payMemo: 'Note',
    advance: 'Advance',
    advanceWarning:
      'This is more than {name} has earned so far. The difference will be recorded as an advance against future work.',
    allowAdvance: 'Record the difference as an advance',
    exceedsOutstanding: 'That is more than this dentist has earned.',
    partPaid: 'Part paid',
    remaining: 'Remaining',
    paid: 'Paid',
    payments: 'Payments',
    noPayments: 'No payments to this dentist yet.',
    monthly: 'Month by month',
    monthlyNote:
      'Produced, earned and paid are three different figures for the same month and rarely agree. Each is counted separately.',
    month: 'Month',
    produced: 'Produced',
    earned: 'Earned',
    perMonth: 'a month',
    ownerTitle: 'The owner\'s share',
    ownerHint: 'Collected this year, less what everyone else has been paid or is owed. Draws come out of this.',
    profit: 'Profit to date',
    drawn: 'Drawn so far',
    available: 'Available to draw',
    takeDraw: 'Take a draw',
    drawAmount: 'How much?',
    drawNote: 'A draw is not an earning: it is the owner taking part of the profit. It is recorded as its own kind of payment.',
    lessOthers: 'Less commission earned by other dentists',
    lessLab: 'Less laboratory work received',
    lessPurchases: 'Less supplier bills posted',
    lessExpenses: 'Less expenses',
    publicTitle: 'Public title',
    publicTitleHint: 'What patients are told this person does, e.g. Orthodontist.',
    frozenNote:
      'Each accrual stores the rate it was calculated with, so changing the rate never rewrites past earnings.',
    ownOnly: 'You can only view your own statement.',
    noDentists: 'No dentists in this clinic.',
    add: 'Add dentist',
    addTitle: 'Add a dentist',
    addHint: 'For a dentist who will not sign in to OxyFlows. They appear in the calendar, treatments and pay like any dentist. To give someone their own sign-in, invite them from Clinic → Staff and permissions instead.',
    fullName: 'Full name',
    titleLabel: 'Title or specialty',
    titlePlaceholder: 'e.g. Orthodontist',
    noSignIn: 'No sign-in',
    added: 'Dentist added.',
    hours: 'Working hours',
    hoursTitle: 'Working hours · {name}',
    hoursHint: 'The days and times this dentist works, in the clinic’s time. Patients can only request times inside them on the public page.',
    works: 'Works',
    off: 'Day off',
    addPeriod: 'Add a second period',
    removePeriod: 'Remove',
    from: 'From',
    to: 'To',
    hoursSaved: 'Working hours saved.',
    hoursNone: 'No hours set yet',
    noAccruals: 'No commission accrued in this period.',
    production: 'Production',
    reverse: 'Reverse',
    reverseTitle: 'Reverse this payment',
    reverseHint:
      'What it paid is owed again and the money comes back into the books. The payment stays on the statement, marked reversed.',
    reverseReason: 'Reason',
    reversed: 'Reversed',
    draw: 'Draw',
    owesClinic: 'Owes the clinic',
  },
  settings: {
    title: 'Clinic',
    general: 'General',
    publicPage: 'Public page',
    staff: 'Staff and permissions',
    auditWho: 'Who',
    inviteeName: 'Their name',
    catalogue: 'Treatment catalogue',
    chairs: 'Chairs',
    addChair: 'Add a chair',
    noChairs: 'No chairs yet. The calendar can split by chair once there are some.',
    resourceKind: 'Kind',
    resourceKinds: { chair: 'Chair', room: 'Room', equipment: 'Equipment' },
    audit: 'Audit log',
    devices: 'Devices',
    subscription: 'Subscription',
    name: 'Name',
    timezone: 'Timezone',
    currency: 'Currency',
    countryCode: 'Country dialling code',
    description: 'Description',
    address: 'Address',
    publish: 'Publish the public page',
    showTeam: 'Show the team publicly',
    bookingMode: 'Online booking',
    bookingModes: {
      disabled: 'Disabled',
      request: 'Request, staff confirm',
      verified_auto: 'Confirm automatically',
    },
    bookingHint: 'Requests are the safe default: staff confirm every booking before it is final.',
    save: 'Save changes',
    saved: 'Saved.',
    role: 'Role',
    permissions: 'Permissions',
    effective: 'effective',
    delegationNote: 'You can only grant permissions you hold yourself.',
    editPermissions: 'Edit permissions',
    permissionsFor: 'Permissions for {name}',
    fromRole: 'From role',
    granted: 'Granted',
    revoked: 'Revoked',
    permissionOrigin: {
      role: 'role',
      granted: 'added',
      revoked: 'removed',
    },
    permissionHelp:
      'A role is a starting point. Ticking a permission the role does not include adds it for this person; unticking one the role does include takes it away from them. Either way the role itself is untouched.',
    roleTemplate: 'Role',
    resetToRole: 'Reset to the role',
    permissionAreas: {
      patient: 'Patients',
      clinical: 'Clinical records',
      rx: 'Prescriptions',
      appointment: 'Appointments and scheduling',
      schedule: 'Appointments and scheduling',
      finance: 'Money and invoicing',
      commission: 'Dentist pay',
      dentist: 'Dentist pay',
      inventory: 'Inventory and purchasing',
      lab: 'Laboratory',
      clinic: 'Clinic administration',
      device: 'Devices and offline',
      sync: 'Devices and offline',
      platform: 'Platform',
      other: 'Other',
    },
    cannotDelegate: 'You do not hold this permission yourself, so you cannot grant it.',
    noChanges: 'Nothing to save.',
    procedures: 'Procedure codes',
    procedureCode: 'Code',
    addProcedure: 'New procedure code',
    installStandard: 'Add the standard procedures',
    emptyCatalogue: 'No procedures yet. Nothing can be recorded as a treatment until there is at least one — add the standard set, then put your prices on it.',
    noPrice: 'no price',
    priceHint: 'Applies to new treatments only; anything already recorded keeps the price it was charged at.',
    requiresTooth: 'Recorded against a specific tooth',
    specialties: 'What the clinic practises',
    specialtiesHint: 'Decides which charts the patient file shows and which standard procedures are offered. Switching one on adds its standard procedures at zero price.',
    specialty: { dental: 'Dental', dermatology: 'Dermatology', aesthetics: 'Aesthetic medicine' },
    specialtyLocked: 'Dermatology and aesthetic medicine are part of the Center plan.',
    catalogueSpecialty: 'Specialty',
    catalogueSpecialtyGeneral: 'General',
    requiresZone: 'Recorded on a face or body zone',
    doseUnit: 'Dose counted in',
    doseUnitNone: 'No dose',
    zoneMark: 'Mark left on the zone when completed',
    zoneMarkNone: 'Leaves no mark',
    zoneMarkHint: 'What the chart shows on the zone once this is done: toxin, filler, laser.',
    impliesLab: 'Needs laboratory work',
    toothOutcome: 'Tooth after completion',
    toothOutcomeHint: 'Completing the treatment marks the tooth with this on the chart. Leave empty if the procedure changes nothing about the tooth.',
    toothOutcomeNone: 'Changes nothing about the tooth',
    storageUsed: 'Storage used',
    plan: 'Plan',
    seats: 'Seats',
    auditNote: 'This log is append-only. The application has no privilege to change or delete it.',
    viewUrl: 'View public page',
    invite: 'Invite someone',
    inviteHint: 'The invitation goes to their email with a link. They create their account with that address, confirming it with a code sent there, or sign in if they already have one. They never see your clinic’s activation code.',
    dentistSeat: 'Works as a dentist',
    invitedDone: 'Invitation recorded for {email}. They create their account with this address, or sign in if they already have one.',
    invitationEmailed: 'Invitation emailed to {email}. The email explains how to create the account, or to sign in if they already have one.',
    resendInvite: 'Send the invitation again',
    inviteResent: 'Sent again',
    emailOff: 'Email is not set up yet',
    everySignIn: 'Ask everyone in this clinic for a code at every sign-in',
    everySignInHint: 'Normally a code by email is asked on a new or untrusted device, and owners are asked every time. With this on, nobody here can trust a device.',
    twoStep: 'Two-step sign-in',
    twoStepEvery: 'After your password, a code is sent to your email at every sign-in.',
    twoStepNewDevices: 'After your password, a code is sent to your email when you sign in on a device you have not trusted in the last 30 days.',
    twoStepOff: 'Email is not set up on the server yet, so sign-in uses your password only.',
    forgetDevices: 'Forget my trusted devices',
    devicesForgotten: '{n} device(s) will ask for a code again',
    account: 'Your account',
    invited: 'Invited',
  },
  platform: {
    title: 'Platform',
    subtitle: 'Every clinic on this installation.',
    whoNote:
      'Only an account marked as a platform operator can open this. A clinic owner is the owner of their own clinic and nothing else — no amount of role editing inside a clinic can reach these pages.',
    clinics: 'Clinics',
    operators: 'Operators',
    codes: 'Activation codes',
    support: 'Support access',
    newClinic: 'New clinic',
    clinicName: 'Clinic name',
    address: 'Web address',
    addressHint: 'Used for the public page at /c/{slug}.',
    ownerEmail: 'Owner email',
    specialties: 'The clinic practises',
    specialtiesHint: 'Its charts and standard procedures follow. Dermatology and aesthetics need the Center plan, or the feature granted.',
    ownerName: 'Owner name',
    months: 'Months',
    plan: 'Plan',
    seats: 'Seats',
    dentists: 'Dentists',
    patients: 'Patients',
    expires: 'Expires',
    expired: 'Expired',
    suspend: 'Suspend',
    suspended: 'Suspended',
    restore: 'Restore',
    suspendTitle: 'Suspend this clinic?',
    suspendNote:
      'Records are preserved and stay readable. The clinic cannot make changes until it is restored.',
    reason: 'Reason',
    reasonHint: 'Recorded in the clinic’s own activity log, where the customer can see it.',
    extend: 'Change plan or extend',
    extendNote: 'Adds to the current end date. A subscription that already lapsed restarts from today.',
    issueCode: 'Issue activation code',
    codeEmail: 'Bound to email',
    codeEmailHint: 'The code can only be redeemed by this address.',
    validDays: 'Valid for (days)',
    codeShownOnce: 'This code is shown once and is never stored in full. Copy it now.',
    copy: 'Copy',
    copied: 'Copied.',
    revoke: 'Revoke',
    codeState: {
      available: 'Available',
      redeemed: 'Redeemed',
      expired: 'Expired',
      revoked: 'Revoked',
    },
    grantSupport: 'Request support access',
    supportNote:
      'Time-boxed and reasoned. The grant is written into the clinic’s own audit log and expires on its own — there is no standing access to forget about.',
    scope: 'Scope',
    scopes: { configuration: 'Configuration', billing: 'Billing', clinical: 'Clinical' },
    hours: 'Hours',
    endAccess: 'End now',
    active: 'Active',
    ended: 'Ended',
    makeOperator: 'Add an operator',
    operatorNote:
      'Operators see every clinic. Another operator has to add or remove you — nobody can change their own access here.',
    you: 'You',
    ownerQuestion: 'Who runs the platform?',
    ownerAnswer:
      'The platform operator: the vendor. They create clinics, set what each has paid for, issue activation codes and can enter a clinic briefly to help. They cannot browse patient records at will — support access is short, reasoned and logged where the customer can see it.',
    noClinics: 'No clinics yet.',
    noCodes: 'No activation codes issued yet.',
    oneAccountNote:
      'One clinic account, in the owner\'s name. The clinic\'s activation code is issued with it and stays visible here; the owner activates with it and sets their own password. Staff are invited by email from inside the clinic and never need a code.',
    ownerCode: 'Owner code',
    ownerCodeTitle: 'Activation code for the owner',
    ownerCodeHint: 'Bound to this clinic and to the owner\'s email. Any earlier code for the owner stops working.',
    clinicCreated: 'The clinic exists. This is its activation code — it stays visible on the clinic\'s page.',
    owner: 'Owner',
    code: 'Activation code',
    noCode: 'No code',
    starts: 'Starts',
    ends: 'Ends',
    dates: 'Subscription dates',
    setDates: 'Set the dates',
    datesHint: 'The first and last day the subscription is valid. The last day is included whole. Leave the dates empty to add months instead.',
    startsOn: 'First day',
    endsOn: 'Last day',
    pause: 'Pause access',
    pauseDays: 'For how many days',
    pauseReason: 'Reason (shown to nobody but operators)',
    pauseNote: 'Nothing is lost. Everyone in the clinic sees that access is paused until the date, and it lifts by itself.',
    paused: 'Paused',
    pausedUntil: 'Paused until',
    resume: 'Resume now',
    expiringSoon: 'Ends soon',
    daysLeft: '{days} days left',
    needsAttention: 'Needs a call',
    nothingNeedsAttention: 'No subscription is ending or paused.',
    details: 'Details',
    users: 'People in this clinic',
    noUsers: 'Nobody yet.',
    activatedOn: 'Activated',
    policy: 'Data policy',
    policyAccepted: 'Accepted on',
    policyNotAccepted: 'Not accepted yet',
    notActivated: 'Not activated yet',
    newCode: 'New code',
    emailCode: 'Email the code to the owner',
    codeEmailed: 'The code was emailed to {email}.',
    codeNotEmailed: 'Email is not set up yet, so the code was not emailed to {email}. Read it out to the owner.',
    emailLanguage: 'Language of the clinic',
    emailLanguageHint: 'The owner’s emails are written in this language first, until they sign in and choose their own.',
    exportClinic: 'Export everything',
    exportHint: 'Every table, every person, every image, in one file you keep. Take it before removing a clinic; it is what brings the clinic back.',
    deleteClinic: 'Remove from the server',
    deleteHint: 'Removes the clinic and all of its data from the server. Only possible after an export has been taken. Restore later by importing that file.',
    typeAddress: 'Type the clinic\'s web address to confirm',
    deleteForever: 'Remove clinic',
    removed: 'The clinic has been removed.',
    importClinic: 'Restore a clinic',
    importHint: 'Choose a clinic export file. The clinic must not already exist.',
    chooseFile: 'Choose file',
    imported: 'Restored {rows} rows and {files} files.',
    licenceNote: 'The clinic\'s code works only while the subscription is valid. The owner can use it again to set a new password.',
    noGrants: 'No support access has been requested.',
  },
  reports: {
    title: 'Reports',
    procedures: 'Procedures',
    proceduresSub: 'What this clinic actually does, by procedure code.',
    grain: { month: 'Month', quarter: 'Quarter', half: 'Half year', year: 'Year' },
    custom: 'Custom',
    from: 'From',
    to: 'To',
    performed: 'Performed',
    billedHint: 'Sum of the prices these treatments were charged at — not what was collected.',
    billed: 'Billed',
    byCount: 'Most performed',
    byValue: 'Highest billed',
    rankNote:
      'The most frequent procedure is usually not the most valuable one, so the two are ranked separately rather than merged into one number.',
    uncoded: 'Without a procedure code',
    uncodedNote: '{n} completed treatment(s) have no catalogue code, so they are grouped separately rather than dropped.',
    distinctCodes: 'Distinct codes',
    allDentists: 'All dentists',
    perDentist: 'By dentist',
    completedOnly: 'Completed work only. A planned treatment is an intention, not activity.',
    noData: 'No completed work in this period.',
  },
  staff: {
    permissions: 'permissions',
  },
  whatsapp: {
    title: 'WhatsApp draft',
    recipient: 'To',
    open: 'Open WhatsApp',
    copy: 'Copy message',
    copied: 'Copied.',
    notSent:
      'This opens WhatsApp with the message ready. Nothing is sent automatically, and the record will say a draft was opened, not that a message was delivered.',
  },
  viewer: {
    title: 'Image viewer',
    close: 'Close',
    previous: 'Previous image',
    next: 'Next image',
    tools: 'Tools',
    pan: 'Move',
    window: 'Brightness and contrast',
    length: 'Measure a length',
    angle: 'Measure an angle',
    calibrate: 'Set the scale',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    fit: 'Fit to screen',
    actual: 'Actual pixels',
    rotate: 'Rotate',
    flipH: 'Flip left\u2013right',
    flipV: 'Flip top\u2013bottom',
    invert: 'Invert',
    reset: 'Reset the view',
    adjustments: 'Adjustments',
    brightness: 'Brightness',
    contrast: 'Contrast',
    gamma: 'Gamma',
    scale: 'Scale',
    notCalibrated: 'No scale yet \u2014 lengths are in pixels. Set it from a known length or the sensor\u2019s pixel size.',
    calibrated: '{value} px per mm ({method})',
    methodKnownLength: 'known length',
    methodPixelSize: 'pixel size',
    knownLength: 'Real length of the line (mm)',
    knownLengthHint: 'Enter the real length of what you just marked.',
    pixelSize: 'Sensor pixel size (\u00b5m)',
    pixelSizeHint: 'From the sensor\u2019s data sheet: 20 \u00b5m means 50 px per mm.',
    apply: 'Apply',
    clearScale: 'Clear',
    measurements: 'Measurements',
    noMeasurements: 'Nothing measured yet.',
    remove: 'Remove',
    lengthHint: 'Click the two ends of what you want to measure.',
    angleHint: 'Click three points \u2014 the angle is measured at the second.',
    calibrateHint: 'Click the two ends of something whose real length you know \u2014 a file, an implant.',
    windowHint: 'Drag: left and right for contrast, up and down for brightness.',
    panHint: 'Drag to move \u00b7 scroll to zoom \u00b7 double-click to fit.',
    save: 'Save measurements',
    saving: 'Saving\u2026',
    saved: 'Saved with the image.',
    unsaved: 'Not saved yet.',
    readOnly: 'You can measure here, but saving with the image needs the imaging permission.',
    discardConfirm: 'Discard the unsaved measurements?',
    openOriginal: 'Open the file',
    px: 'px',
    mm: 'mm',
  },
  sync: {
    title: 'Offline activity',
    sub: 'What the rules decided while a computer worked offline. Nothing here needs resolving: the record stands as the rule left it. Glance, act if you want to, acknowledge.',
    attention: 'To look at',
    forTheRecord: 'For the record',
    nothingOpen: 'Nothing is waiting for a look.',
    nothingYet: 'No offline activity in the last 30 days.',
    showAll: 'Show acknowledged too',
    acknowledge: 'Acknowledge',
    acknowledged: 'Acknowledged',
    by: 'by {name}',
    fromDevice: 'from {device}',
    openPatient: 'Open patient',
    openCalendar: 'Open calendar',
    openFinance: 'Open finance',
    openInventory: 'Open inventory',
    sentFromHere: 'Sent from this computer',
    sentFromHereSub: 'Every change made here and what the server did with it.',
    couldNotSend: 'Could not be applied',
    couldNotSendSub: 'The server refused these; the reason is shown. They stay here for the clinic owner.',
    state: { pending: 'Waiting to send', in_flight: 'Sending', acknowledged: 'Applied', conflict: 'Decided by rule', recovery: 'Refused' },
    noLog: 'Nothing has been sent from this computer yet.',
    rules: 'The rules',
    rulesText: [
      'Field by field, the newest change wins; the change it overtook is kept in the log.',
      'A booking made offline into a slot taken meanwhile is kept and shown in red until reception moves it.',
      'Work invoiced twice keeps its first invoice line; the duplicate line is dropped.',
      'A payment is never dropped: it settles the open invoices oldest-first, and anything beyond them is credit on the account.',
      'Stock may go negative; it says so and asks for a count.',
      'A temporary invoice reference becomes an official number when it reaches the server.',
    ],
    kinds: {
      booking_overlap: 'Booking overlaps another',
      duplicate_booking: 'Same booking twice — one kept',
      possible_duplicate_payment: 'Possible double payment',
      payment_applied: 'Payment applied',
      invoice_numbered: 'Invoice numbered',
      line_dropped: 'Invoice line dropped (already invoiced)',
      invoice_dropped: 'Invoice not created (already invoiced)',
      possible_duplicate_treatment: 'Same procedure recorded twice',
      stock_negative: 'Stock below zero',
      superseded_edit: 'Older edit kept in the log',
      superseded_status: 'Order had moved on',
      not_applied: 'Not applied',
    },
    detail: {
      booking_overlap: 'The slot at {at} was already taken when this booking arrived. It is kept and shown in red; move it or leave it.',
      duplicate_booking: 'The same patient, dentist and time were booked from two computers. One booking is kept.',
      possible_duplicate_payment: '{amount} was recorded again for this patient within two days of another payment of the same amount. Both are kept; reverse one if it was typed twice.',
      payment_applied: '{amount} received: {applied} applied to invoices{credit}.',
      payment_credit: ', {credit} kept as credit on the account',
      invoice_numbered: '{ref} became {number}.',
      line_dropped: '"{description}" was already on another invoice, so it was left off this one.',
      invoice_dropped: 'Every line of {ref} was already invoiced elsewhere, so no invoice was created.',
      possible_duplicate_treatment: '{name} on {tooth} was already recorded that day. Both are kept; remove one if it was recorded twice.',
      stock_negative: '{name} is at {onHand}: the records say less than nothing. Count it and record an adjustment.',
      superseded_edit: '{fields} had changed on the server after this edit was made. The newer value is kept; this one is in the log.',
      superseded_status: 'The order was already at "{kept}"; "{wanted}" was not applied.',
      not_applied: 'The server refused it: {message}',
    },
    flags: {
      superseded: 'newer on server',
      overlap: 'overlap',
      duplicate: 'duplicate',
      line_dropped: 'line dropped',
      invoice_dropped: 'not created',
      not_applied: 'not applied',
      numbered: 'numbered',
      credit: 'credit',
      stock_negative: 'stock below zero',
      possible_duplicate: 'possible duplicate',
    },
    entities: {
      patient: 'Patient', medical_history: 'Medical history', appointment: 'Appointment', treatment: 'Treatment', tooth_observation: 'Chart finding', surface_finding: 'Surface finding', zone_observation: 'Face and body chart', clinical_note: 'Clinical note', attachment: 'Image', invoice: 'Invoice', payment: 'Payment', stock_movement: 'Stock movement', lab_order: 'Lab order',
    },
    actions: { create: 'added', update: 'changed', delete: 'removed', append: 'recorded', post: 'posted' },
    temporaryRef: 'Temporary reference until synced',
  },
  capture: {
    title: 'Capture from sensor',
    sub: 'Through the sensor\'s own driver on this computer. The driver\'s window governs the exposure; the image comes back here.',
    source: 'Sensor',
    noSources: 'No sensor driver was found on this computer. Install the sensor\'s TWAIN driver, or import from its export folder below.',
    simulatorNote: 'The simulated sensor produces a labelled synthetic image for trying the workflow. It is never a real radiograph.',
    showUi: 'Show the driver\'s capture window',
    capture: 'Capture',
    capturing: 'Waiting for the sensor…',
    captured: '{n} image(s) captured',
    failed: 'The capture did not produce an image.',
    errors: {
      bridge_missing: 'The sensor bridge is missing from this installation.',
      source_not_found: 'That sensor is no longer listed. Refresh the list.',
      opends_failed: 'The driver could not be opened. Is the sensor connected and its own software closed?',
      enable_failed: 'The driver refused to start a capture.',
      timeout: 'No image arrived before the time ran out.',
      cancelled: 'The capture was cancelled in the driver\'s window.',
      no_image: 'The driver signalled an image but none could be read.',
      no_dsm: 'No TWAIN manager is installed for this driver.',
    },
    preview: 'Captured image',
    pixelSize: 'Pixel size from the driver: {um} µm — the viewer\'s scale is set from it.',
    noPixelSize: 'The driver did not report the pixel size; set the scale in the viewer from a known length.',
    saveAll: 'Save to the patient',
    saving: 'Saving…',
    saved: 'Saved',
    discard: 'Discard',
    kind: 'Kind',
    tooth: 'Tooth',
    folderTitle: 'Import from the sensor software\'s folder',
    folderSub: 'When the sensor software saves its images to a folder, new files appear here.',
    folderPath: 'Folder',
    folderMissing: 'That folder does not exist on this computer.',
    folderNone: 'No folder is set yet.',
    folderEmpty: 'No new images in the folder.',
    folderNew: 'new',
    import: 'Import',
    refresh: 'Refresh',
    settings: 'Sensor settings',
    transfer: 'Transfer mode',
    transferMemory: 'Raw samples (keeps 16-bit)',
    transferNative: 'Windows bitmap (most compatible)',
    selfTest: 'Test the bridge',
    selfTestResult: 'Bridge {arch}: {result}',
    save: 'Save settings',
  },
  imaging: {
    title: 'Imaging',
    upload: 'Upload image',
    uploading: 'Uploading…',
    file: 'File',
    acquiredOn: 'Taken on',
    acquiredOnHint: 'When the image was taken, which is not always today.',
    tooLarge: 'larger than the {limit} limit',
    wholeMouthNote: 'This kind covers the whole mouth, so it is not attached to a single tooth.',
    addImage: 'Add image',
    noneYet: 'No images in this patient’s file yet.',
    kinds: {
      panoramic: 'Panoramic (OPG)',
      cbct: 'CBCT',
      periapical: 'Periapical',
      bitewing: 'Bitewing',
      cephalometric: 'Cephalometric',
      intraoral_photo: 'Intraoral photo',
      extraoral_photo: 'Extraoral photo',
      document: 'Document',
      consent: 'Consent form',
      report: 'Report',
      other: 'Other',
    },

    library: 'Patient imaging',
    noImages: 'No images for this patient.',
    kind: 'Type',
    source: 'Source',
    linkedTeeth: 'Linked teeth',
    dicomNote: 'DICOM studies are stored with their provenance. A CBCT viewer is not yet available.',
  },
  sources: {
    title: 'Patient sources',
    sub: 'Where your patients come from. Reception picks one when registering a patient; the reports count them.',
    label: 'Source',
    none: 'Not recorded',
    referredBy: 'Referred by a dentist',
    referredByOption: 'Referred by {name}',
    dentistsGroup: 'Dentists of the clinic',
    detail: 'Source detail',
    detailHint: 'Optional: who exactly sent them',
    add: 'Add source',
    nameEn: 'Name (English)',
    nameAr: 'Name (Arabic)',
    retire: 'Retire',
    restore: 'Use again',
    remove: 'Remove',
    retired: 'Retired',
    patientsCount: '{n} patients',
    retiredInstead: 'Patients already have this source, so it was retired instead of removed.',
    empty: 'No sources yet.',
    save: 'Save',
    cancel: 'Cancel',
  },
  journey: {
    tab: 'Visits',
    upcoming: 'Coming up',
    nextPlan: 'Planned for next time',
    plan: 'Treatment plan (not done yet)',
    none: 'No visits yet.',
    done: 'Done this visit',
    nothingDone: 'No work recorded for this visit.',
    addNote: 'Add visit note',
    whatDone: 'What was done',
    whatDoneHint: 'Treatment done, findings, anything the next dentist should know',
    next: 'Plan for the next visit',
    nextHint: 'Optional',
    share: 'Share with the clinic’s dentists',
    shareHint: 'A shared note is shown to the other dentists without your name. The clinic’s managers see every note.',
    private: 'Private',
    shared: 'Shared',
    privateHint: 'Only you and the clinic’s managers see this note',
    aDentist: 'A dentist of the clinic',
    you: 'You',
    makeShared: 'Share',
    makePrivate: 'Make private',
    save: 'Save note',
    saving: 'Saving…',
    cancel: 'Cancel',
    with: 'with {name}',
    followedUp: 'Followed up: {s}',
    notesHidden: 'Notes are for the clinical team.',
    status: {
      completed: 'Attended',
      checked_in: 'Checked in',
      in_treatment: 'In treatment',
      no_show: 'Missed',
      cancelled: 'Cancelled',
      scheduled: 'Scheduled',
      confirmed: 'Confirmed',
      visit: 'Visit',
      work: 'Work recorded',
      note: 'Note',
    },
  },
  missed: {
    title: 'Missed appointments to follow up',
    sub: 'Patients who did not come. Call them, book them again, or close the case.',
    none: 'No missed appointments to follow up.',
    open: 'To follow up',
    all: 'All',
    missedOn: 'Missed {date}',
    nextBooked: 'Booked again for {date}',
    note: 'Note (optional)',
    reopen: 'Reopen',
    by: 'by {name}',
    count: '{n} to follow up',
    openList: 'Open the list',
    action: { contacted: 'Contacted', rebooked: 'Rebooked', closed: 'Close' },
    status: { contacted: 'Contacted', rebooked: 'Rebooked', closed: 'Closed' },
  },
  insights: {
    title: 'Reports',
    sub: 'What happened in the clinic over a period.',
    ownScope: 'Your own figures: the patients you saw or brought, your procedures and your missed appointments.',
    tabs: { overview: 'Overview', patients: 'New patients', dentists: 'Dentists', procedures: 'Procedures', missed: 'Missed appointments' },
    newPatients: 'New patients',
    patientsSeen: 'Patients seen',
    completed: 'Appointments attended',
    noShows: 'Missed appointments',
    noShowRate: 'Missed rate',
    rateHint: 'Of the appointments that were due (attended or missed), the share that was missed.',
    cancelled: 'Cancelled',
    procedures: 'Procedures done',
    bySource: 'By source',
    byReferrer: 'Referred by dentists',
    notRecorded: 'Not recorded',
    list: 'The patients',
    registered: 'Registered',
    dentist: 'Dentist',
    referred: 'Patients referred',
    value: 'Value of procedures',
    count: 'Count',
    patientsCol: 'Patients',
    procedure: 'Procedure',
    exportCsv: 'Download (CSV)',
    empty: 'Nothing in this period.',
    allDentists: 'All dentists',
    financeLink: 'Money (invoices, payments, expenses) is in Finance, under Reports.',
    followup: 'Follow-up',
    notFollowed: 'Not followed up',
  },
  dictation: {
    start: 'Dictate',
    stop: 'Stop',
    listening: 'Listening… speak, then press Stop',
    transcribing: 'Turning speech into text…',
    loadingModel: 'Preparing speech recognition on this computer (first time only: about 550 MB, or 240 MB without a graphics card)…',
    unsupported: 'Dictation is not available in this browser.',
    denied: 'Allow the microphone to dictate.',
    failed: 'The recording could not be turned into text. Try again, closer to the microphone.',
    settingTitle: 'Voice notes',
    settingSub: 'Dentists can dictate notes instead of typing them.',
    device: 'On this computer (private)',
    deviceHint: 'The voice never leaves the computer. The first use downloads the speech model once (the most accurate one the computer can run). Works in the Windows app and offline.',
    browser: 'Browser speech service (faster)',
    browserHint: 'Very accurate and instant, but the voice is sent to Google or Microsoft to be turned into text. Not available in the Windows app, which always uses the private option.',
    off: 'Off',
    save: 'Save',
    saved: 'Saved',
  },
  patientEdit: {
    edit: 'Edit details',
    title: 'Edit patient details',
  },
};

export type ModuleDict = typeof en;

const ar: ModuleDict = {
  zones: {
    group: {
      upper_face: 'أعلى الوجه',
      eyes: 'العينان',
      mid_face: 'منتصف الوجه',
      lips: 'الشفاه والفم',
      lower_face: 'أسفل الوجه',
      neck: 'الرقبة',
      trunk: 'الجذع',
      arms: 'الذراعان',
      legs: 'الساقان',
    },
    zoneList: 'المناطق',
    hoverHint: 'مرّر المؤشر فوق الرسم أو اختر منطقة من القائمة.',
    recorded: 'مسجَّل',
    title: 'مخطط الوجه والجسم',
    subtitle: 'اختر منطقة لتسجيل ملاحظة أو إجراء بجرعته أو صورة.',
    region: 'المنطقة',
    view: 'المنظر',
    face: 'الوجه',
    body: 'الجسم',
    front: 'من الأمام',
    back: 'من الخلف',
    sideR: 'يمين',
    sideL: 'يسار',
    viewNote: 'من الأمام: يمين المريض على يسارك.',
    viewNoteBack: 'من الخلف: يمين المريض على يمينك.',
    selectZone: 'اختر منطقة',
    selectZoneHint: 'انقر على جزء من الرسم. تُسجَّل عليه الملاحظات والإجراءات.',
    currentFinding: 'الملاحظة الحالية',
    noFinding: 'لا شيء مسجَّل على هذه المنطقة.',
    procedures: 'الإجراءات المنفَّذة هنا',
    noProcedures: 'لا إجراءات مسجَّلة على هذه المنطقة.',
    planned: 'مخطَّط هنا',
    photos: 'صور هذه المنطقة',
    noPhotos: 'لا صور مرتبطة بهذه المنطقة بعد.',
    recordFinding: 'تسجيل ملاحظة',
    addProcedure: 'إضافة إجراء',
    addPhoto: 'إضافة صورة',
    findingLabel: 'الملاحظة',
    severityLabel: 'الشدة',
    dose: 'الجرعة',
    doseHint: 'كما أُعطيت: 20 وحدة، 1.5 مل، 400 طلقة.',
    doseInvalid: 'أدخل الجرعة رقمًا، مثل 20 أو 1.5.',
    doseUnit: 'الوحدة',
    product: 'المنتج',
    productHint: 'العلامة والتركيز كما على العبوة.',
    lot: 'رقم التشغيلة',
    lotHint: 'رقم التشغيلة، للتتبع.',
    noProcedureItems: 'لا إجراءات جلدية أو تجميلية في القائمة بعد. أضف الإجراءات القياسية من العيادة، قائمة المعالجات.',
    willMark: 'إكمال هذا يسجّل {mark} على {zone} في المخطط.',
    legendFinding: 'ملاحظة مسجَّلة',
    legendProcedure: 'إجراءات منفَّذة (العدد)',
    zone: {
      scalp: 'فروة الرأس', forehead: 'الجبهة', glabella: 'ما بين الحاجبين', temple_r: 'الصدغ الأيمن', temple_l: 'الصدغ الأيسر',
      brow_r: 'الحاجب الأيمن', brow_l: 'الحاجب الأيسر', periorbital_r: 'محيط العين اليمنى', periorbital_l: 'محيط العين اليسرى',
      tear_trough_r: 'الأخدود الدمعي الأيمن', tear_trough_l: 'الأخدود الدمعي الأيسر', nose: 'الأنف', cheek_r: 'الخد الأيمن', cheek_l: 'الخد الأيسر',
      nasolabial_r: 'الثنية الأنفية الشفوية اليمنى', nasolabial_l: 'الثنية الأنفية الشفوية اليسرى', upper_lip: 'الشفة العليا', lower_lip: 'الشفة السفلى',
      perioral: 'محيط الفم', marionette_r: 'خط الماريونيت الأيمن', marionette_l: 'خط الماريونيت الأيسر', chin: 'الذقن',
      jawline_r: 'خط الفك الأيمن', jawline_l: 'خط الفك الأيسر', masseter_r: 'العضلة الماضغة اليمنى', masseter_l: 'العضلة الماضغة اليسرى', neck: 'الرقبة',
      decolletage: 'أعلى الصدر', shoulder_r: 'الكتف الأيمن', shoulder_l: 'الكتف الأيسر', chest: 'الصدر', abdomen: 'البطن',
      flank_r: 'الخاصرة اليمنى', flank_l: 'الخاصرة اليسرى', back_upper: 'أعلى الظهر', back_lower: 'أسفل الظهر',
      arm_upper_r: 'العضد الأيمن', arm_upper_l: 'العضد الأيسر', forearm_r: 'الساعد الأيمن', forearm_l: 'الساعد الأيسر',
      hand_r: 'اليد اليمنى', hand_l: 'اليد اليسرى', underarm_r: 'الإبط الأيمن', underarm_l: 'الإبط الأيسر', bikini: 'منطقة البكيني',
      buttock_r: 'الردف الأيمن', buttock_l: 'الردف الأيسر', thigh_r: 'الفخذ الأيمن', thigh_l: 'الفخذ الأيسر', knee_r: 'الركبة اليمنى', knee_l: 'الركبة اليسرى',
      lower_leg_r: 'الساق اليمنى', lower_leg_l: 'الساق اليسرى', foot_r: 'القدم اليمنى', foot_l: 'القدم اليسرى',
    },
    finding: {
      normal: 'طبيعي', wrinkles_dynamic: 'تجاعيد حركية', wrinkles_static: 'تجاعيد ثابتة', volume_loss: 'فقدان حجم', laxity: 'ترهّل',
      asymmetry: 'عدم تناظر', pigmentation: 'تصبّغ', melasma: 'كلف', acne: 'حب الشباب', acne_scars: 'ندبات حب الشباب', scar: 'ندبة',
      lesion: 'آفة', mole: 'شامة', redness: 'احمرار', rosacea: 'وردية', enlarged_pores: 'مسام واسعة', unwanted_hair: 'شعر غير مرغوب',
      hair_loss: 'تساقط شعر', cellulite: 'سيلوليت', fat_deposit: 'تراكم دهني', stretch_marks: 'تشققات', tattoo: 'وشم', other: 'أخرى',
    },
    mark: {
      toxin: 'توكسين البوتولينوم', filler: 'فيلر', booster: 'محفّز بشرة', prp: 'بلازما غنية بالصفيحات', mesotherapy: 'ميزوثيرابي', thread: 'خيوط',
      laser: 'ليزر', ipl: 'ضوء نبضي مكثف', peel: 'تقشير كيميائي', microneedling: 'ميكرونيدلينغ', rf: 'ترددات راديوية', hifu: 'هايفو',
      cryolipolysis: 'تجميد الدهون', excision: 'استئصال', cryotherapy: 'معالجة بالتبريد', electrocautery: 'كيّ كهربائي', biopsy: 'خزعة',
      injection_other: 'حقن', other: 'إجراء آخر',
    },
    unit: { units: 'وحدة', ml: 'مل', mg: 'ملغ', sessions: 'جلسة', shots: 'طلقة', threads: 'خيط', cm2: 'سم²', vials: 'عبوة', minutes: 'دقيقة' },
    severity: { mild: 'خفيف', moderate: 'متوسط', severe: 'شديد' },
  },
  chart: {
    title: 'المخطط السني',
    subtitle: 'اختر سنّاً ثم سطحاً أو أكثر لتسجيل الموجودات أو المعالجات أو الملاحظات أو الصور.',
    tooth: 'السن',
    dentition: 'الإطباق',
    dentitionKind: { permanent: 'دائم', primary: 'لبني', mixed: 'مختلط' },
    charted: 'مسجّل',
    selectTooth: 'اختر سنّاً',
    selectToothHint: 'انقر على سنّ في المخطط، ثم انقر على أسطحه لتحديدها.',
    selectSurfacesFirst: 'حدّد سطحاً واحداً على الأقل أولاً.',
    surfacesSelected: 'سطح محدد',
    currentStatus: 'الحالة الحالية',
    recordFinding: 'تسجيل موجودة',
    recordSurface: 'تسجيل موجودة سطحية',
    addTreatment: 'إضافة معالجة',
    addNote: 'إضافة ملاحظة',
    capture: 'التقاط صورة',
    save: 'حفظ',
    media: 'صور هذا السن',
    noMedia: 'لا توجد صور مرتبطة بهذا السن بعد.',
    sharedFileNote: 'هذه هي الملفات نفسها الموجودة في مكتبة صور المريض، وليست نسخاً.',
    treatments: 'المعالجات',
    noTreatments: 'لا توجد معالجات مسجّلة على هذا السن.',
    simulated: 'محاكاة',
    procedure: 'الإجراء',
    price: 'السعر لهذا المريض',
    removeTreatment: 'إزالة المعالجة',
    removeTreatmentBody:
      'لمعالجة سُجِّلت بالخطأ. إن كانت قد علّمت السنّ على المخطط يُعاد المخطط إلى ما كان عليه — كإدخال جديد، فيحتفظ السجل بالخطأ وتصحيحه. غير ممكن بعد إدراجها في فاتورة.',
    completedIsFixed: 'الإجراء والسنّ ثابتان بعد الإكمال لأن المخطط كُتب منهما. أزِل المعالجة وسجّل الصحيحة إن كانا خاطئين.',
    priceAfterBilled: 'مفوترة سابقاً: تحتفظ الفاتورة بسعرها كما حُوسب. هذا يغيّر السجل فقط.',
    chargeable: 'محاسبة المريض على هذه المعالجة',
    chargeableHint:
      'مفعّلة افتراضياً. ألغِ التحديد لإعادة عمل ضمن الضمان أو مجاملة أو عمل مسجّل للتوثيق فقط — يبقى ضمن الإحصاءات لكن لا يمكن وضعه على فاتورة أبداً.',
    priceHint: 'يبدأ من سعر الدليل. عدّله عند وجود حسم أو سعر متفق عليه.',
    cataloguePrice: 'سعر الدليل',
    priceInvalid: 'أدخل السعر رقماً، مثل 67.50.',
    performedOn: 'تاريخ التنفيذ',
    performedOnHint: 'الافتراضي هو اليوم. عدّله إذا كنت تسجّل عملاً تمّ سابقاً.',
    willChart: 'إكمال هذا يجعل السن {tooth} بحالة {status} على المخطط.',
    markCompleted: 'تعليمه مكتملاً',
    captureHint: 'تظهر الصور الملتقطة على سنٍّ هنا وفي معرض ذلك السن — ملف واحد لا نسختان.',
    simulatorWarning:
      'هذه أداة محاكاة للالتقاط. تنتج صورة اصطناعية وليست صورة شعاعية حقيقية، ولا يجوز استخدامها للتشخيص إطلاقاً.',
    confirmPatient: 'أؤكد أن هذا هو المريض والسن الصحيحان.',
    status: {
      sound: 'سليم',
      caries: 'نخر',
      restored: 'محشو',
      crowned: 'تاج',
      implant: 'زرعة',
      root_canal: 'معالجة لبية',
      missing: 'مفقود',
      unerupted: 'غير بازغ',
      retained_primary: 'لبني محتبس',
      extracted: 'مقلوع',
      bridge_pontic: 'جسر معلّق',
      veneer: 'قشرة',
      fractured: 'مكسور',
      watch: 'قيد المراقبة',
    },
    surface: {
      mesial: 'إنسي',
      distal: 'وحشي',
      buccal: 'دهليزي',
      labial: 'شفوي',
      palatal: 'حنكي',
      lingual: 'لساني',
      occlusal: 'إطباقي',
      incisal: 'قاطع',
    },
    finding: {
      sound: 'سليم',
      caries: 'نخر',
      restoration: 'حشوة',
      sealant: 'مادة سادّة',
      fracture: 'كسر',
      wear: 'تآكل',
      discolouration: 'تلوّن',
      watch: 'مراقبة',
    },
  },
  treatmentStatus: {
    planned: 'مخطط',
    accepted: 'مقبول',
    in_progress: 'قيد التنفيذ',
    completed: 'مكتمل',
    cancelled: 'ملغى',
    amended: 'معدّل',
  },
  finance: {
    title: 'المالية',
    invoices: 'الفواتير',
    payments: 'الدفعات',
    expenses: 'المصاريف',
    reports: 'التقارير',
    newInvoice: 'فاتورة جديدة',
    recordPayment: 'تسجيل دفعة',
    recordExpense: 'تسجيل مصروف',
    number: 'الرقم',
    patient: 'المريض',
    issued: 'تاريخ الإصدار',
    due: 'الاستحقاق',
    total: 'الإجمالي',
    paid: 'المدفوع',
    outstanding: 'المتبقي',
    status: 'الحالة',
    draft: 'مسودة',
    posted: 'مرحّلة',
    expenseCategory: { rent: 'الإيجار', utilities: 'الخدمات (كهرباء وماء)', salaries: 'الرواتب', supplies: 'المستلزمات', maintenance: 'الصيانة', marketing: 'التسويق', other: 'أخرى' } as Record<string, string>,
    method: 'طريقة الدفع',
    amount: 'المبلغ',
    reference: 'المرجع',
    category: 'الفئة',
    description: 'الوصف',
    post: 'ترحيل الفاتورة',
    postHint: 'الترحيل يخصّص الرقم ويجمّد البنود. التصحيح بعدها يحتاج إشعار دائن.',
    billed: 'المفوتر',
    billedHint: 'قيمة الفواتير المرحّلة في الفترة',
    collected: 'المحصّل',
    collectedHint: 'المبالغ المستلمة فعلياً',
    outstandingHint: 'مرحّل وغير مدفوع',
    expensesHint: 'المصاريف التشغيلية المسجّلة',
    labCost: 'تكلفة المخبر',
    commission: 'عمولة الأطباء',
    profitEstimate: 'الربح التقديري',
    profitHint: 'المحصّل ناقص المصاريف وتكلفة المخبر والعمولات. تقدير وليس ربحاً محاسبياً.',
    aging: 'أعمار الذمم',
    current: 'لم يستحق بعد',
    byDentist: 'حسب الطبيب',
    byCategory: 'حسب الفئة',
    monthly: 'التحصيل الشهري',
    uniquePatients: 'المرضى',
    uniqueTeeth: 'الأسنان',
    procedures: 'الإجراءات',
    produced: 'الإنتاج',
    noInvoices: 'لا توجد فواتير بعد.',
    noPayments: 'لا توجد دفعات مسجّلة.',
    noExpenses: 'لا توجد مصاريف مسجّلة.',
    selectTreatments: 'معالجات مكتملة غير مفوترة',
    noUnbilled: 'لا توجد معالجات غير مفوترة لهذا المريض.',
    alreadyBilled: 'مفوترة سابقاً',
    notChargeable: 'غير قابلة للفوترة',
    notChargeableWhy:
      'مسجّلة كعمل لا يُحاسب عليه المريض هنا — نشاط سابق أو إعادة ضمن الضمان أو مجاملة. أعد فتح المعالجة لجعلها قابلة للفوترة.',
    hiddenNotChargeable: '{n} معالجة مكتملة مُعلَّمة غير قابلة للفوترة ولا يمكن فوترتها.',
    showNotChargeable: 'إظهارها',
    hideNotChargeable: 'إخفاؤها',
    installments: 'تقسيط',
    none: 'بدون',
    payFull: 'سداد كامل',
    invoiceNumber: 'الفاتورة',
    settleThis: 'تسجيل دفعة',
    settleAll: 'تسوية الرصيد',
    creditOnAccount: 'رصيد دائن',
    creditOnAccountHint: 'مدفوع مسبقاً ولم يُطبَّق على فاتورة بعد.',
    searchPayments: 'بحث',
    searchPaymentsHint: 'اسم، مرجع، فئة…',
    direction: 'وارد / صادر',
    inAndOut: 'وارد وصادر',
    moneyIn: 'وارد',
    moneyOut: 'صادر',
    showReversed: 'إظهار الدفعات المعكوسة وعكوسها',
    ownerDrawFor: 'سحب المالك — {name}',
    unbilled: 'أعمال غير مفوترة',
    unbilledHint:
      'مكتملة وقابلة للفوترة ولم توضع على فاتورة قط. لا يعلم الجانب المالي بها حتى تُفوتر.',
    noUnbilled2: 'كل ما أُنجز قد فُوتر.',
    invoiceNow: 'فوترة الآن',
    payables: 'المستحقات علينا',
    payablesHint:
      'ما تدين به العيادة للموردين والمختبرات، من فواتير مُرحَّلة وأعمال مختبر مستلمة — الوقائع نفسها التي رُحِّل بها الدفتر.',
    nothingOwed: 'لا يوجد مستحقات لأي جهة.',
    owedToSuppliers: 'مستحق للموردين',
    owedToLabs: 'مستحق للمختبرات',
    pendingAtLab: 'عند المختبر، غير مستحق بعد',
    openBills: '{n} فاتورة',
    pay: 'دفع',
    paySupplier: 'دفع للمورّد',
    payLaboratory: 'دفع للمختبر',
    payingTo: 'الدفع إلى {name}. المتبقي: {amount}.',
    paidOut: 'أين ذهب المال',
    paidOutHint: 'المبالغ الصادرة في الفترة حسب المستلم. الدفعات المعكوسة مستثناة من الطرفين.',
    toDentists: 'للأطباء',
    toSuppliers: 'للموردين',
    toLaboratories: 'للمختبرات',
    refunds: 'مبالغ معادة للمرضى',
    otherOut: 'أخرى',
    expensesByCategory: 'المصاريف حسب الفئة',
    counterparty: 'إلى / من',
    settlementFor: 'تسوية طبيب — {name}',
    supplierPayment: 'مورّد — {name}',
    laboratoryPayment: 'مختبر — {name}',
    settlingInvoice: 'سداد الفاتورة {number}. ما يزيد عن رصيدها يذهب إلى فواتير هذا المريض المفتوحة الأخرى.',
    receivedOn: 'تاريخ الاستلام',
    receivedOnHint: 'الافتراضي هو اليوم. عدّله إذا وصل المال سابقاً.',
    onHold: 'قيد الانتظار',
    cardOnHold: 'الدفع بالبطاقة متفق عليه لكنه غير مُفعَّل بعد، لذا لا يمكن استلام غير النقد اليوم.',
    methods: {
      cash: 'نقداً',
      card: 'بطاقة',
      transfer: 'حوالة بنكية',
      cheque: 'شيك',
      other: 'أخرى',
    },
  },
  remove: {
    confirm: 'إزالة',
    delete: 'حذف',
    edit: 'تعديل',
    archive: 'أرشفة',
    restore: 'استعادة',
    typeToConfirm: 'اكتب {word} للتأكيد.',
    wasDeleted: 'تم الحذف.',
    wasDeactivated: 'تم الاحتفاظ به وتعليمه غير نشط — لن يظهر في القوائم ويبقى سجله صحيحاً.',
    keptBecause: 'تعذّر حذفه لأنه مرتبط بـ',
    cannotUndo: 'لا يمكن التراجع عن هذا.',
    deletePatient: 'حذف المريض',
    deletePatientBody:
      'فقط لسجل أُنشئ بالخطأ. لا يمكن حذف مريض له أي سجل — أرشفه بدلاً من ذلك، فيبقى كل شيء ويخرج من القائمة.',
    archivePatient: 'أرشفة المريض',
    archivePatientBody: 'يخرج من قائمة المرضى مع الاحتفاظ بكل سجلاته. يمكنك إعادته في أي وقت.',
    restorePatient: 'استعادة المريض',
    deleteInvoice: 'حذف مسودة الفاتورة',
    deleteInvoiceBody:
      'المسودة بلا رقم ولم تمسّ الحسابات، فلا يضيع شيء. تعود المعالجات المدرجة فيها غير مفوترة.',
    postedCannotDelete:
      'لا يمكن حذف فاتورة مُرحَّلة — الفجوة في الترقيم هي ما يبحث عنه المدقّق. أصدر إشعار دائن بدلاً من ذلك.',
    voidPayment: 'عكس الدفعة',
    voidPaymentBody:
      'تبقى الدفعة في السجل ويُكتب قيد معاكس بجانبها. تعود الفواتير التي سُدِّدت بها غير مدفوعة.',
    voidReason: 'ما سبب العكس؟',
    voidReasonHint: 'يُحفظ مع العكس. اكتب ما يكفي ليفهمه من يقرأ الدفاتر بعد سنة.',
    commissionWarning:
      'هذه الدفعة حقّقت عمولة قدرها {amount} وقد صُرفت فعلاً. عكسها يجعلها ديناً يُخصم من التسوية القادمة.',
    deleteSupplier: 'إزالة المورّد',
    deleteLaboratory: 'إزالة المختبر',
    deleteMaterial: 'إزالة الصنف',
    deletePurchase: 'حذف المشترى',
    deleteLabOrder: 'حذف طلب المختبر',
    deleteOrderBody: 'ممكن فقط ما دام مسودة. بعد الإرسال ألغِه بدلاً من ذلك.',
    editSupplier: 'تعديل المورّد',
    editLaboratory: 'تعديل المختبر',
    editMaterial: 'تعديل الصنف',
    retired: 'متقاعد',
    reactivate: 'إعادة تفعيل',
    reversal: 'عكس',
    reversed: 'معكوسة',
  },
  transfer: {
    title: 'الاستيراد والتصدير',
    exportTitle: 'تصدير إلى إكسل',
    exportHint: 'كل تصدير ملف .xlsx حقيقي: المبالغ أرقام يمكن جمعها، والتواريخ تواريخ.',
    importTitle: 'استيراد المرضى',
    importHint: 'أدخل المرضى من النظام الذي كنت تستخدمه.',
    download: 'تنزيل',
    downloading: 'جارٍ التحضير…',
    downloaded: '{name} — {rows} صف',
    needsPermission: 'لا تملك صلاحية لهذا.',
    chooseDentist: 'الطبيب',
    from: 'من',
    to: 'إلى',
    wholeHistory: 'اترك التواريخ فارغة للحصول على كل شيء.',
    getTemplate: 'تنزيل النموذج',
    templateHint:
      'املأ صفاً لكل مريض وارفع الملف أدناه. يمكن استخدام جدولك الخاص أيضاً — تُقرأ أسماء الأعمدة الشائعة.',
    chooseFile: 'اختر ملفاً',
    checking: 'جارٍ الفحص…',
    checked: '{total} صف: {ready} جاهز، {duplicates} تكرار محتمل، {errors} فيها مشكلة.',
    importReady: 'استيراد {n} مريض',
    importing: 'جارٍ الاستيراد…',
    imported: 'تم استيراد {n} مريض.',
    nothingToImport: 'لا يوجد في هذا الملف ما يمكن استيراده بعد.',
    line: 'الصف',
    rowStatus: 'الحالة',
    rowName: 'الاسم',
    rowProblem: 'ملاحظات',
    statusReady: 'سيُضاف',
    statusDuplicate: 'تكرار محتمل',
    statusError: 'تعذّرت القراءة',
    looksLike: 'يشبه {name} ({number})',
    includeDuplicates: 'أضف التكرارات المحتملة أيضاً',
    includeDuplicatesHint:
      'مغلق افتراضياً. العائلات تتشارك أرقام الهواتف والأب والابن يتشاركان الاسم، فالحكم لك.',
    exportKinds: {
      patients: 'المرضى',
      'patients-full': 'المرضى — كل شيء في صف واحد',
      'patients-by-dentist': 'المرضى حسب الطبيب',
      treatments: 'الإجراءات',
      invoices: 'الفواتير',
      payments: 'الدفعات',
      inventory: 'المخزون',
      appointments: 'المواعيد',
    },
  },
  rx: {
    title: 'الوصفات',
    new: 'وصفة جديدة',
    state: 'الحالة',
    items: 'الأدوية',
    addItem: 'إضافة دواء',
    medicine: 'الدواء',
    dose: 'الجرعة',
    route: 'طريق الإعطاء',
    frequency: 'التكرار',
    duration: 'المدة',
    instructions: 'التعليمات',
    quantity: 'الكمية',
    check: 'تشغيل فحص السلامة',
    checking: 'جارٍ الفحص',
    issue: 'إصدار الوصفة',
    cancelRx: 'إلغاء الوصفة',
    template: 'قالب',
    useTemplate: 'استخدام قالب',
    templateNote: 'اختيار القالب يُشغّل دائماً فحصاً جديداً لهذا المريض.',
    states: {
      draft: 'مسودة',
      blocked: 'محجوبة',
      checked: 'مفحوصة',
      issued: 'صادرة',
      cancelled: 'ملغاة',
    },
    findings: 'نتائج الفحص',
    noFindings: 'لم تُسجَّل أي نتائج.',
    blockedNotice: 'الإصدار محجوب. راجع النتائج وعدّل المسودة.',
    staleNotice: 'تغيّر السجل منذ آخر فحص. أعد الفحص قبل الإصدار.',
    syntheticNotice:
      'فحص السلامة يعمل على بيانات اختبارية غير سريرية. لا يجوز الاعتماد عليه في رعاية المرضى.',
    notGuarantee: 'الفحص المكتمل هو دعم للقرار ويتطلب مراجعة الطبيب. وهو ليس ضماناً للسلامة.',
    noPrescriptions: 'لا توجد وصفات بعد.',
    printDraft: 'معاينة مسودة',
    none: 'لا توجد وصفات لهذا المريض.',
    saveDraft: 'حفظ كمسودة',
    edit: 'تعديل',
    discard: 'إلغاء المسودة',
    removeItem: 'حذف',
    stale: 'يحتاج إعادة فحص',
    unresolved: 'غير موجود في البيانات',
    mine: 'خاصة بي',
    clinicWide: 'للعيادة كلها',
    templateHint: 'يملأ الأدوية. تبقى الوصفة تُفحص لهذا المريض على أي حال.',
    saveAsTemplate: 'حفظ هذه الأدوية كقالب',
    templates: 'القوالب',
    newTemplate: 'قالب جديد',
    editTemplate: 'تعديل القالب',
    templateName: 'اسم القالب',
    templateScope: 'من يمكنه استخدامه',
    personalScope: 'أنا فقط',
    clinicScope: 'الجميع في العيادة',
    clinicScopeNeeds: 'النشر لكل العيادة يتطلب صلاحية إدارة القوالب؛ القالب الشخصي لا يتطلب شيئاً.',
    templateDescription: 'الوصف',
    noTemplates: 'لا قوالب بعد. احفظ واحداً من وصفة، أو اكتب واحداً هنا.',
    archiveTemplate: 'إزالة القالب',
    scopeBadge: { personal: 'لي', clinic: 'العيادة' },
    saveAsTemplateHint: 'اتركه فارغاً إن لم ترد الحفظ. أعطه اسماً لإعادة استخدامه.',
    blockedTitle: 'لا يمكن إصدار هذه الوصفة.',
    blockedHelp: 'عدّل الأدوية وأعد الفحص، أو ألغِ المسودة. تبقى في السجل في الحالتين — لا يُحذف شيء.',
    editVoidsCheck: 'التعديل يُلغي الفحص السابق. هذه ليست الأدوية التي فحصها، لذلك يجب أن يُعاد.',
    syntheticNote: 'هذا الحكم صدر عن بيانات اختبار غير سريرية ولا يجوز الاعتماد عليه في رعاية المرضى.',
    printValid: 'طباعة الوصفة',
  },
  lab: {
    title: 'المخبر',
    orders: 'الطلبات',
    openOrders: 'أعمال مخبرية مستحقة',
    noneForPatient: 'لا يوجد عمل مخبري لهذا المريض.',
    raiseFromChart: 'خطّط تاجاً أو جهازاً أو عملاً مخبرياً على المخطط، ثم أنشئ الطلب من قسم المخبر.',
    patientCostNote: 'كلفة العيادة لدى المخبر منفصلة عمّا يُحمَّل على المريض. لا يجتمعان في بند واحد أبداً.',
    laboratories: 'المخابر',
    newOrder: 'طلب مخبري جديد',
    newLab: 'إضافة مخبر',
    item: 'العمل',
    teeth: 'الأسنان',
    shade: 'اللون',
    due: 'الاستحقاق',
    labCost: 'تكلفة المخبر',
    patientCharge: 'أجر المريض',
    editOrder: 'تعديل الطلب',
    correctStatus: 'تصحيح الحالة',
    correctStatusHint:
      'لضغطة خاطئة — «مُستلم من العيادة» ولم يكن، أو «مُستلم» قبل يوم. يمكن الانتقال إلى أي حالة؛ يُحفظ السبب في سجل الطلب، وتتبعه مستحقات المختبر.',
    correctionReason: 'لماذا؟',
    payableRecorded: 'مستحق للمختبر',
    pendingAtLab: 'عند المختبر',
    labCostEditNote: 'تغيير الكلفة بعد وصول العمل يغيّر ما تدين به العيادة للمختبر؛ الدفاتر تتبع ذلك.',
    costNote: 'تكلفة العيادة وأجر المريض منفصلان. فاتورة المخبر لا تكرّر أجر المعالجة.',
    advance: 'تقديم',
    whatsapp: 'مسودة واتساب',
    remind: 'إرسال تذكير',
    overdue: 'متأخر',
    outstanding: 'المتبقي',
    noOrders: 'لا توجد طلبات مخبرية.',
    noLabs: 'لا توجد مخابر بعد.',
    states: {
      draft: 'مسودة',
      awaiting_pickup: 'بانتظار الاستلام',
      collected: 'تم الاستلام',
      in_preparation: 'قيد التحضير',
      ready: 'جاهز',
      received: 'وصل للعيادة',
      fitting: 'التجربة',
      adjustment_requested: 'طُلب تعديل',
      returned_for_adjustment: 'أُعيد للتعديل',
      completed: 'مكتمل',
      cancelled: 'ملغى',
    },
  },
  inventory: {
    title: 'المستودع',
    materials: 'المواد',
    suppliers: 'الموردون',
    purchases: 'المشتريات',
    alerts: 'التنبيهات',
    sku: 'الرمز',
    name: 'الاسم',
    onHand: 'المتوفر',
    threshold: 'حد إعادة الطلب',
    location: 'الموقع',
    value: 'القيمة',
    unitCost: 'كلفة الوحدة',
    newMaterial: 'إضافة مادة',
    newSupplier: 'إضافة مورّد',
    askPriceTitle: 'طلب سعر',
    askWhichSupplier: 'أي مورّد؟',
    askQuantity: 'كم عبوة؟',
    newItemInline: '+ صنف جديد',
    postBill: 'ترحيل الفاتورة',
    postBillHint: 'يسجّل المستحق للمورّد وتاريخ الأسعار. لا يؤثر على المخزون.',
    receiveGoods: 'استلام البضاعة',
    receiveGoodsHint: 'يُدخل الأصناف إلى المخزون. لا يؤثر على رصيد المورّد.',
    notPosted: 'غير مُرحَّلة',
    outstandingWith: 'المتبقي لدى {name}',
    contact: 'جهة الاتصال',
    address: 'العنوان',
    newPurchase: 'تسجيل شراء',
    consume: 'تسجيل استهلاك',
    askPrice: 'طلب سعر',
    level: { ok: 'متوفر', low: 'منخفض', out: 'نفد', negative: 'سالب' },
    negativeNote: 'الرصيد السالب يعني اختلاف السجلات عن الواقع. أجرِ جرداً للمطابقة.',
    packs: 'عبوات',
    packSize: 'وحدات في العبوة',
    supplier: 'المورّد',
    invoiceNumber: 'رقم فاتورة المورّد',
    action: 'الإجراء',
    actions: {
      post_bill: 'ترحيل الفاتورة فقط',
      confirm_receipt: 'تأكيد استلام البضاعة فقط',
      post_and_receive: 'ترحيل واستلام',
    },
    actionHint: 'ترحيل الفاتورة ينشئ الذمة. استلام البضاعة ينشئ المخزون. وهما حدثان منفصلان.',
    received: 'مستلم',
    billedOnly: 'مفوتر وغير مستلم',
    noMaterials: 'لا توجد مواد بعد.',
    noSuppliers: 'لا يوجد موردون بعد.',
    noPurchases: 'لا توجد مشتريات مسجّلة.',
    costingMethod: 'طريقة التكلفة',
    lowStock: 'منخفض أو نافد',
    expiring: 'قارب على الانتهاء',
    allGood: 'لا شيء يحتاج انتباهاً.',
    quantity: 'الكمية',
    search: 'ابحث بالاسم أو الرمز أو الموقع',
    allCategories: 'كل التصنيفات',
    newCategory: 'تصنيف جديد',
    noMatches: 'لا شيء يطابق هذا الترشيح.',
    usedOn: 'تاريخ الاستخدام',
    usedOnHint: 'الافتراضي هو الآن. عدّله إذا كنت تسجّل شيئاً حدث سابقاً.',
    newItem: 'صنف جديد',
    packSizeHint: 'عدد الوحدات الأساسية في العبوة — علبة فيها ١٠٠ قفاز تساوي ١٠٠.',
    newItemHint:
      'يضيف الصنف إلى الدليل بدون رصيد. يدخل المخزون عبر استلام بضاعة، فإضافة صنف لا تُنشئ كمية أبداً.',
    itemName: 'اسم الصنف',
    baseUnit: 'الوحدة الأساسية',
    packUnit: 'وحدة العبوة',
    reorderLevel: 'حد إعادة الطلب',
    category: 'التصنيف',
    created: 'أُضيف الصنف.',
  },
  dentists: {
    title: 'الأطباء',
    statement: 'كشف الحساب',
    assignedPatients: 'المرضى المُسندون',
    completedProcedures: 'الإجراءات المكتملة',
    commission: 'العمولة',
    accrued: 'المستحق',
    settled: 'المسوّى',
    unsettled: 'غير المسوّى',
    basis: 'الأساس',
    rate: 'النسبة',
    agreements: 'الاتفاقيات',
    newAgreement: 'اتفاقية جديدة',
    settle: 'تسوية العمولة',
    effectiveFrom: 'سارية من',
    deductLab: 'خصم تكلفة المخبر',
    basisKind: {
      collected: 'على المحصّل',
      billed: 'على المفوتر',
      net_of_lab: 'صافي بعد المخبر',
      salary: 'راتب شهري',
      profit: 'المالك — أرباح العيادة',
    },
    basisExplainer: {
      collected: 'يُدفع على المال الذي حصّلته العيادة فعلاً. الفاتورة التي لا يسددها المريض لا تمنح الطبيب شيئاً، فلا شيء يُسترد لاحقاً.',
      billed: 'يُدفع على ما جرت فوترته، سواء سدّد المريض أم لا. العيادة تتحمل المخاطرة إن لم يسدد.',
      net_of_lab: 'يُدفع على المحصّل بعد خصم كلفة المخبر للعمل نفسه.',
      salary: 'مبلغ شهري ثابت، مستقل عمّا أُنجز أو حُصّل.',
      profit: 'المالك. لا يُستحق شيء لكل إجراء: ما يتبقى بعد دفع كل طبيب آخر ومورّد ومختبر ومصروف هو له، ويسحب منه كما يشاء.',
    },
    whoCarriesRisk: 'من يتحمل المخاطرة إذا لم يسدد المريض',
    riskClinic: 'العيادة',
    riskShared: 'مشتركة مع الطبيب',
    salaryPayNote:
      'الطبيب براتب يُدفع له عن الشهر، لا عن إجراءات بعينها. أرقام الإنتاج أدناه للاطلاع، لا للدفع على أساسها.',
    payAgainstSalary: 'دفعة على الراتب',
    salaryOwed: 'الراتب المستحق',
    commissionOwed: 'النسبة المستحقة',
    payKind: 'طريقة أجر الطبيب',
    commissionOnly: 'نسبة',
    salaryOnly: 'راتب',
    salaryAndCommission: 'راتب ونسبة',
    monthlySalary: 'الراتب الشهري',
    salary: 'الراتب',
    salaryNote: 'الراتب والنسبة اتفاقان منفصلان يعملان معاً. تعديل أحدهما لا ينهي الآخر.',
    postSalary: 'ترحيل الراتب',
    unpostedSalary: 'راتب لم يُرحّل بعد',
    unpostedSalaryNote: 'يستحق الراتب عند انتهاء الشهر، وترحيله قرار صريح — فتح هذه الصفحة لا ينشئه.',
    proRated: 'محسوب على {days} من {of} يوماً',
    payDentist: 'دفع للطبيب',
    payAmount: 'المبلغ المدفوع',
    payFull: 'دفع كامل المستحق',
    payPart: 'دفع مبلغ محدد',
    payNote:
      'أنت تحدد المبلغ. يُسدَّد به أقدم الاستحقاقات أولاً، والاستحقاق المدفوع جزئياً يبقى ظاهراً بما تبقّى منه.',
    payMethod: 'طريقة الدفع',
    payMemo: 'ملاحظة',
    advance: 'سلفة',
    advanceWarning: 'هذا أكثر مما استحقه {name} حتى الآن. سيُسجَّل الفارق سلفة على عمل قادم.',
    allowAdvance: 'تسجيل الفارق كسلفة',
    exceedsOutstanding: 'المبلغ أكبر مما استحقه هذا الطبيب.',
    partPaid: 'مدفوع جزئياً',
    remaining: 'المتبقي',
    paid: 'المدفوع',
    payments: 'الدفعات',
    noPayments: 'لا توجد دفعات لهذا الطبيب بعد.',
    monthly: 'شهراً بشهر',
    monthlyNote: 'المنجز والمستحق والمدفوع ثلاثة أرقام مختلفة للشهر نفسه ونادراً ما تتطابق. كلٌّ يُحسب وحده.',
    month: 'الشهر',
    produced: 'المنجز',
    earned: 'المستحق',
    perMonth: 'شهرياً',
    ownerTitle: 'حصة المالك',
    ownerHint: 'المحصَّل هذه السنة ناقص ما دُفع أو استُحق لكل الآخرين. تُخصم السحوبات من هذا.',
    profit: 'الربح حتى الآن',
    drawn: 'المسحوب حتى الآن',
    available: 'المتاح للسحب',
    takeDraw: 'سحب مبلغ',
    drawAmount: 'كم؟',
    drawNote: 'السحب ليس أجراً: هو أخذ المالك جزءاً من الربح. يُسجَّل كنوع خاص من الدفعات.',
    lessOthers: 'ناقص عمولات الأطباء الآخرين',
    lessLab: 'ناقص أعمال المختبر المستلمة',
    lessPurchases: 'ناقص فواتير الموردين المُرحَّلة',
    lessExpenses: 'ناقص المصاريف',
    publicTitle: 'اللقب المعلن',
    publicTitleHint: 'ما يُعرَض للمرضى عن تخصص هذا الشخص، مثل: أخصائي تقويم.',
    frozenNote: 'كل استحقاق يحفظ النسبة التي حُسب بها، فتغيير النسبة لا يعيد كتابة الأرباح السابقة.',
    ownOnly: 'يمكنك الاطلاع على كشف حسابك فقط.',
    noDentists: 'لا يوجد أطباء في هذه العيادة.',
    add: 'إضافة طبيب',
    addTitle: 'إضافة طبيب',
    addHint: 'لطبيب لن يسجّل الدخول إلى أُكسي فلوز. يظهر في التقويم والمعالجات والأجور مثل أي طبيب. لمنح شخص حساب دخول خاصًا به، ادعُه من العيادة ← الفريق والصلاحيات بدلًا من ذلك.',
    fullName: 'الاسم الكامل',
    titleLabel: 'اللقب أو الاختصاص',
    titlePlaceholder: 'مثلًا: تقويم الأسنان',
    noSignIn: 'دون حساب دخول',
    added: 'تمت إضافة الطبيب.',
    hours: 'ساعات العمل',
    hoursTitle: 'ساعات العمل · {name}',
    hoursHint: 'الأيام والأوقات التي يعمل فيها هذا الطبيب، بتوقيت العيادة. لا يستطيع المرضى طلب مواعيد إلا ضمنها في الصفحة العامة.',
    works: 'يعمل',
    off: 'يوم عطلة',
    addPeriod: 'إضافة فترة ثانية',
    removePeriod: 'حذف',
    from: 'من',
    to: 'إلى',
    hoursSaved: 'تم حفظ ساعات العمل.',
    hoursNone: 'لم تُحدَّد ساعات بعد',
    noAccruals: 'لا توجد عمولة مستحقة في هذه الفترة.',
    production: 'الإنتاج',
    reverse: 'عكس',
    reverseTitle: 'عكس هذه الدفعة',
    reverseHint: 'ما سُدِّد يعود مستحقاً ويعود المال إلى الدفاتر. تبقى الدفعة في الكشف مع علامة أنها عُكست.',
    reverseReason: 'السبب',
    reversed: 'معكوسة',
    draw: 'سحب',
    owesClinic: 'مدين للعيادة',
  },
  settings: {
    title: 'العيادة',
    general: 'عام',
    publicPage: 'الصفحة العامة',
    staff: 'الفريق والصلاحيات',
    auditWho: 'مَن',
    inviteeName: 'اسم الشخص',
    catalogue: 'قائمة المعالجات',
    chairs: 'الكراسي',
    addChair: 'إضافة كرسي',
    noChairs: 'لا توجد كراسي بعد. يمكن للتقويم التقسيم حسب الكرسي عند إضافتها.',
    resourceKind: 'النوع',
    resourceKinds: { chair: 'كرسي', room: 'غرفة', equipment: 'جهاز' },
    audit: 'سجل التدقيق',
    devices: 'الأجهزة',
    subscription: 'الاشتراك',
    name: 'الاسم',
    timezone: 'المنطقة الزمنية',
    currency: 'العملة',
    countryCode: 'رمز الاتصال الدولي',
    description: 'الوصف',
    address: 'العنوان',
    publish: 'نشر الصفحة العامة',
    showTeam: 'إظهار الفريق للعموم',
    bookingMode: 'الحجز الإلكتروني',
    bookingModes: {
      disabled: 'معطّل',
      request: 'طلب يؤكده الموظفون',
      verified_auto: 'تأكيد تلقائي',
    },
    bookingHint: 'الطلبات هي الخيار الآمن: يؤكد الموظفون كل حجز قبل اعتماده.',
    save: 'حفظ التغييرات',
    saved: 'تم الحفظ.',
    role: 'الدور',
    permissions: 'الصلاحيات',
    effective: 'فعّالة',
    delegationNote: 'لا يمكنك منح صلاحيات لا تملكها أنت.',
    editPermissions: 'تعديل الصلاحيات',
    permissionsFor: 'صلاحيات {name}',
    fromRole: 'من الدور',
    granted: 'ممنوحة',
    revoked: 'مسحوبة',
    permissionOrigin: { role: 'الدور', granted: 'مضافة', revoked: 'مسحوبة' },
    permissionHelp:
      'الدور نقطة بداية. تحديد صلاحية لا يتضمنها الدور يضيفها لهذا الشخص، وإلغاء تحديد صلاحية يتضمنها الدور يسحبها منه. الدور نفسه لا يتغير.',
    roleTemplate: 'الدور',
    resetToRole: 'إعادة إلى الدور',
    permissionAreas: {
      patient: 'المرضى',
      clinical: 'السجل السريري',
      rx: 'الوصفات',
      appointment: 'المواعيد والجدولة',
      schedule: 'المواعيد والجدولة',
      finance: 'المال والفوترة',
      commission: 'أجور الأطباء',
      dentist: 'أجور الأطباء',
      inventory: 'المخزون والمشتريات',
      lab: 'المخبر',
      clinic: 'إدارة العيادة',
      device: 'الأجهزة والعمل دون اتصال',
      sync: 'الأجهزة والعمل دون اتصال',
      platform: 'المنصة',
      other: 'أخرى',
    },
    cannotDelegate: 'لا تملك هذه الصلاحية بنفسك، لذلك لا يمكنك منحها.',
    noChanges: 'لا شيء لحفظه.',
    procedures: 'رموز الإجراءات',
    procedureCode: 'الرمز',
    addProcedure: 'رمز إجراء جديد',
    installStandard: 'إضافة الإجراءات القياسية',
    emptyCatalogue: 'لا توجد إجراءات بعد. لا يمكن تسجيل أي معالجة حتى يوجد إجراء واحد على الأقل — أضف الإجراءات القياسية ثم ضع أسعارك.',
    noPrice: 'بلا سعر',
    priceHint: 'يُطبَّق على المعالجات الجديدة فقط؛ ما سُجِّل سابقاً يحتفظ بسعره كما حُوسب.',
    requiresTooth: 'يُسجَّل على سنّ محدد',
    specialties: 'ما تمارسه العيادة',
    specialtiesHint: 'يحدد المخططات التي يعرضها ملف المريض والإجراءات القياسية المتاحة. تفعيل تخصص يضيف إجراءاته القياسية بسعر صفر.',
    specialty: { dental: 'طب الأسنان', dermatology: 'الأمراض الجلدية', aesthetics: 'الطب التجميلي' },
    specialtyLocked: 'الأمراض الجلدية والطب التجميلي جزء من خطة المركز.',
    catalogueSpecialty: 'التخصص',
    catalogueSpecialtyGeneral: 'عام',
    requiresZone: 'يُسجَّل على منطقة من الوجه أو الجسم',
    doseUnit: 'تُحسب الجرعة بـ',
    doseUnitNone: 'بلا جرعة',
    zoneMark: 'العلامة التي تبقى على المنطقة عند الإكمال',
    zoneMarkNone: 'لا يترك علامة',
    zoneMarkHint: 'ما يعرضه المخطط على المنطقة بعد الانتهاء: توكسين، فيلر، ليزر.',
    impliesLab: 'يتطلب عمل مختبر',
    toothOutcome: 'حالة السنّ بعد الإنجاز',
    toothOutcomeHint: 'عند إكمال المعالجة يُحدَّث المخطط إلى هذه الحالة. اتركه فارغاً إن كان الإجراء لا يغيّر السنّ.',
    toothOutcomeNone: 'لا يغيّر السنّ',
    storageUsed: 'المساحة المستخدمة',
    plan: 'الباقة',
    seats: 'المقاعد',
    auditNote: 'هذا السجل للإضافة فقط. التطبيق لا يملك صلاحية تعديله أو حذفه.',
    viewUrl: 'عرض الصفحة العامة',
    invite: 'دعوة شخص',
    inviteHint: 'تصل الدعوة إلى بريده مع رابط. يُنشئ حسابه بهذا العنوان ويؤكده برمز يُرسل إليه، أو يسجّل الدخول إن كان لديه حساب. لا يرى رمز تفعيل عيادتك أبدًا.',
    dentistSeat: 'يعمل كطبيب أسنان',
    invitedDone: 'سُجّلت الدعوة لـ {email}. يُنشئ حسابه بهذا العنوان، أو يسجّل الدخول إن كان لديه حساب.',
    invitationEmailed: 'أُرسلت الدعوة إلى {email}. تشرح الرسالة كيفية إنشاء الحساب، أو تسجيل الدخول إن كان لديه حساب.',
    resendInvite: 'أرسل الدعوة مرة أخرى',
    inviteResent: 'أُرسلت مرة أخرى',
    emailOff: 'البريد الإلكتروني غير مُعدّ بعد',
    everySignIn: 'اطلب رمزًا من الجميع في هذه العيادة عند كل تسجيل دخول',
    everySignInHint: 'عادةً يُطلب رمز بالبريد على جهاز جديد أو غير موثوق، ويُطلب من المالكين في كل مرة. مع تفعيل هذا الخيار، لا يمكن لأحد هنا الوثوق بجهاز.',
    twoStep: 'الدخول بخطوتين',
    twoStepEvery: 'بعد كلمة المرور، يُرسل رمز إلى بريدك عند كل تسجيل دخول.',
    twoStepNewDevices: 'بعد كلمة المرور، يُرسل رمز إلى بريدك عند الدخول من جهاز لم تثق به خلال آخر 30 يومًا.',
    twoStepOff: 'البريد الإلكتروني غير مُعدّ على الخادم بعد، لذا يتم الدخول بكلمة المرور فقط.',
    forgetDevices: 'انسَ أجهزتي الموثوقة',
    devicesForgotten: '{n} جهاز سيطلب رمزًا مرة أخرى',
    account: 'حسابك',
    invited: 'مدعو',
  },
  platform: {
    title: 'المنصة',
    subtitle: 'كل العيادات على هذا التثبيت.',
    whoNote:
      'لا يفتح هذه الصفحة إلا حساب مُعلَّم كمشغّل للمنصة. مالك العيادة مالكٌ لعيادته وحدها — ولا يمكن لأي تعديل أدوار داخل عيادة أن يصل إلى هنا.',
    clinics: 'العيادات',
    operators: 'المشغّلون',
    codes: 'رموز التفعيل',
    support: 'وصول الدعم',
    newClinic: 'عيادة جديدة',
    clinicName: 'اسم العيادة',
    address: 'العنوان الإلكتروني',
    addressHint: 'يُستخدم للصفحة العامة على /c/{slug}.',
    ownerEmail: 'بريد المالك',
    specialties: 'تمارس العيادة',
    specialtiesHint: 'تتبعها المخططات والإجراءات القياسية. الأمراض الجلدية والتجميل يحتاجان خطة المركز، أو منح الميزة.',
    ownerName: 'اسم المالك',
    months: 'عدد الأشهر',
    plan: 'الباقة',
    seats: 'المقاعد',
    dentists: 'الأطباء',
    patients: 'المرضى',
    expires: 'تنتهي',
    expired: 'منتهية',
    suspend: 'تعليق',
    suspended: 'معلّقة',
    restore: 'إعادة التفعيل',
    suspendTitle: 'تعليق هذه العيادة؟',
    suspendNote: 'تُحفظ السجلات وتبقى قابلة للقراءة. لا تستطيع العيادة إجراء تغييرات حتى تُعاد.',
    reason: 'السبب',
    reasonHint: 'يُسجَّل في سجل نشاط العيادة نفسه، حيث يراه العميل.',
    extend: 'تغيير الباقة أو التمديد',
    extendNote: 'يُضاف إلى تاريخ الانتهاء الحالي. الاشتراك المنتهي يبدأ من اليوم.',
    issueCode: 'إصدار رمز تفعيل',
    codeEmail: 'مرتبط بالبريد',
    codeEmailHint: 'لا يمكن استخدام الرمز إلا من هذا العنوان.',
    validDays: 'صالح لمدة (أيام)',
    codeShownOnce: 'يُعرض هذا الرمز مرة واحدة ولا يُخزَّن كاملاً أبداً. انسخه الآن.',
    copy: 'نسخ',
    copied: 'تم النسخ.',
    revoke: 'إلغاء',
    codeState: { available: 'متاح', redeemed: 'مُستخدم', expired: 'منتهٍ', revoked: 'مُلغى' },
    grantSupport: 'طلب وصول دعم',
    supportNote:
      'محدود بمدة وله سبب مكتوب. يُسجَّل المنح في سجل تدقيق العيادة نفسه وينتهي تلقائياً — لا يوجد وصول دائم يُنسى.',
    scope: 'النطاق',
    scopes: { configuration: 'الإعدادات', billing: 'الفوترة', clinical: 'السريري' },
    hours: 'ساعات',
    endAccess: 'إنهاء الآن',
    active: 'فعّال',
    ended: 'منتهٍ',
    makeOperator: 'إضافة مشغّل',
    operatorNote: 'يرى المشغّلون كل العيادات. يجب أن يضيفك أو يزيلك مشغّل آخر — لا أحد يغيّر وصوله بنفسه.',
    you: 'أنت',
    ownerQuestion: 'من يدير المنصة؟',
    ownerAnswer:
      'مشغّل المنصة: المزوّد. ينشئ العيادات، ويحدد ما دفعته كل واحدة، ويصدر رموز التفعيل، ويستطيع الدخول إلى عيادة لفترة قصيرة للمساعدة. لا يستطيع تصفّح سجلات المرضى متى شاء — وصول الدعم قصير وله سبب ويُسجَّل حيث يراه العميل.',
    noClinics: 'لا توجد عيادات بعد.',
    noCodes: 'لم تُصدَر رموز تفعيل بعد.',
    oneAccountNote:
      'حساب واحد للعيادة باسم المالك. يصدر رمز تفعيل العيادة معه ويبقى ظاهراً هنا؛ يفعّل المالك به ويضع كلمة مروره بنفسه. يُدعى الموظفون بالبريد الإلكتروني من داخل العيادة ولا يحتاجون رمزاً.',
    ownerCode: 'رمز المالك',
    ownerCodeTitle: 'رمز التفعيل للمالك',
    ownerCodeHint: 'مرتبط بهذه العيادة وببريد المالك. أي رمز سابق للمالك يتوقف عن العمل.',
    clinicCreated: 'أُنشئت العيادة. هذا رمز تفعيلها — ويبقى ظاهراً في صفحة العيادة.',
    owner: 'المالك',
    code: 'رمز التفعيل',
    noCode: 'لا رمز',
    starts: 'يبدأ',
    ends: 'ينتهي',
    dates: 'تواريخ الاشتراك',
    setDates: 'تحديد التواريخ',
    datesHint: 'أول يوم وآخر يوم يسري فيهما الاشتراك. اليوم الأخير مشمول كاملاً. اترك التاريخين فارغين لإضافة أشهر بدلاً من ذلك.',
    startsOn: 'أول يوم',
    endsOn: 'آخر يوم',
    pause: 'إيقاف الوصول مؤقتاً',
    pauseDays: 'لكم يوماً',
    pauseReason: 'السبب (لا يراه إلا المشغّلون)',
    pauseNote: 'لا يُفقد شيء. يرى الجميع في العيادة أن الوصول موقوف حتى التاريخ، ويُرفع تلقائياً.',
    paused: 'موقوف مؤقتاً',
    pausedUntil: 'موقوف حتى',
    resume: 'استئناف الآن',
    expiringSoon: 'ينتهي قريباً',
    daysLeft: 'بقي {days} يوماً',
    needsAttention: 'يحتاج اتصالاً',
    nothingNeedsAttention: 'لا اشتراك ينتهي أو موقوف.',
    details: 'التفاصيل',
    users: 'الأشخاص في هذه العيادة',
    noUsers: 'لا أحد بعد.',
    activatedOn: 'فُعّل',
    policy: 'سياسة البيانات',
    policyAccepted: 'قُبلت في',
    policyNotAccepted: 'لم تُقبل بعد',
    notActivated: 'لم يُفعَّل بعد',
    newCode: 'رمز جديد',
    emailCode: 'أرسل الرمز إلى المالك بالبريد',
    codeEmailed: 'أُرسل الرمز إلى {email}.',
    codeNotEmailed: 'البريد الإلكتروني غير مُعدّ بعد، لذا لم يُرسل الرمز إلى {email}. اقرأه للمالك.',
    emailLanguage: 'لغة العيادة',
    emailLanguageHint: 'تُكتب رسائل المالك بهذه اللغة أولاً، إلى أن يسجّل الدخول ويختار لغته.',
    exportClinic: 'تصدير كل شيء',
    exportHint: 'كل الجداول وكل الأشخاص وكل الصور في ملف واحد تحتفظ به. خذه قبل إزالة عيادة؛ فهو ما يعيدها.',
    deleteClinic: 'إزالة من الخادم',
    deleteHint: 'يزيل العيادة وكل بياناتها من الخادم. لا يمكن ذلك إلا بعد أخذ نسخة تصدير. يمكن الاستعادة لاحقاً باستيراد ذلك الملف.',
    typeAddress: 'اكتب عنوان العيادة على الويب للتأكيد',
    deleteForever: 'إزالة العيادة',
    removed: 'أُزيلت العيادة.',
    importClinic: 'استعادة عيادة',
    importHint: 'اختر ملف تصدير عيادة. يجب ألا تكون العيادة موجودة.',
    chooseFile: 'اختيار ملف',
    imported: 'استُعيد {rows} صفاً و{files} ملفاً.',
    licenceNote: 'يعمل رمز العيادة ما دام الاشتراك سارياً. يمكن للمالك استخدامه مجدداً لوضع كلمة مرور جديدة.',
    noGrants: 'لم يُطلب أي وصول دعم.',
  },
  reports: {
    title: 'التقارير',
    procedures: 'الإجراءات',
    proceduresSub: 'ما تقوم به العيادة فعلاً، بحسب رمز الإجراء.',
    grain: { month: 'شهر', quarter: 'ربع سنة', half: 'نصف سنة', year: 'سنة' },
    custom: 'مخصص',
    from: 'من',
    to: 'إلى',
    performed: 'المنفَّذ',
    billedHint: 'مجموع الأسعار التي حُمّلت على هذه المعالجات — وليس ما جرى تحصيله.',
    billed: 'المفوتر',
    byCount: 'الأكثر تنفيذاً',
    byValue: 'الأعلى فوترة',
    rankNote: 'الإجراء الأكثر تكراراً ليس عادةً الأعلى قيمة، لذلك يُرتَّبان منفصلين بدل دمجهما في رقم واحد.',
    uncoded: 'بلا رمز إجراء',
    uncodedNote: '{n} من المعالجات المكتملة بلا رمز في الدليل، فتُعرَض منفصلة بدل إسقاطها.',
    distinctCodes: 'رموز مختلفة',
    allDentists: 'كل الأطباء',
    perDentist: 'حسب الطبيب',
    completedOnly: 'العمل المكتمل فقط. المعالجة المخطَّطة نيّة وليست نشاطاً.',
    noData: 'لا يوجد عمل مكتمل في هذه الفترة.',
  },
  staff: {
    permissions: 'صلاحية',
  },
  whatsapp: {
    title: 'مسودة واتساب',
    recipient: 'إلى',
    open: 'فتح واتساب',
    copy: 'نسخ الرسالة',
    copied: 'تم النسخ.',
    notSent:
      'سيفتح واتساب والرسالة جاهزة. لا يُرسل شيء تلقائياً، وسيُسجَّل أن مسودة فُتحت، لا أن رسالة سُلّمت.',
  },
  viewer: {
    title: 'عارض الصور',
    close: 'إغلاق',
    previous: 'الصورة السابقة',
    next: 'الصورة التالية',
    tools: 'الأدوات',
    pan: 'تحريك',
    window: 'السطوع والتباين',
    length: 'قياس طول',
    angle: 'قياس زاوية',
    calibrate: 'ضبط المقياس',
    zoomIn: 'تكبير',
    zoomOut: 'تصغير',
    fit: 'ملاءمة الشاشة',
    actual: 'الحجم الفعلي',
    rotate: 'تدوير',
    flipH: 'قلب يمين\u2013يسار',
    flipV: 'قلب أعلى\u2013أسفل',
    invert: 'عكس الألوان',
    reset: 'إعادة ضبط العرض',
    adjustments: 'الضبط',
    brightness: 'السطوع',
    contrast: 'التباين',
    gamma: 'غاما',
    scale: 'المقياس',
    notCalibrated: 'لا مقياس بعد \u2014 الأطوال بالبكسل. اضبطه من طول معروف أو من حجم بكسل الحساس.',
    calibrated: '{value} بكسل لكل مم ({method})',
    methodKnownLength: 'طول معروف',
    methodPixelSize: 'حجم البكسل',
    knownLength: 'الطول الحقيقي للخط (مم)',
    knownLengthHint: 'أدخل الطول الحقيقي لما حدّدته للتو.',
    pixelSize: 'حجم بكسل الحساس (ميكرومتر)',
    pixelSizeHint: 'من ورقة مواصفات الحساس: 20 ميكرومتر تعني 50 بكسل لكل مم.',
    apply: 'تطبيق',
    clearScale: 'مسح',
    measurements: 'القياسات',
    noMeasurements: 'لا قياسات بعد.',
    remove: 'إزالة',
    lengthHint: 'انقر على طرفَي ما تريد قياسه.',
    angleHint: 'انقر على ثلاث نقاط \u2014 تُقاس الزاوية عند الثانية.',
    calibrateHint: 'انقر على طرفَي شيء تعرف طوله الحقيقي \u2014 مبرد، زرعة.',
    windowHint: 'اسحب: يميناً ويساراً للتباين، أعلى وأسفل للسطوع.',
    panHint: 'اسحب للتحريك \u00b7 مرّر للتكبير \u00b7 انقر مرتين للملاءمة.',
    save: 'حفظ القياسات',
    saving: 'جارٍ الحفظ\u2026',
    saved: 'حُفظت مع الصورة.',
    unsaved: 'لم تُحفظ بعد.',
    readOnly: 'يمكنك القياس هنا، لكن الحفظ مع الصورة يحتاج صلاحية التصوير.',
    discardConfirm: 'تجاهل القياسات غير المحفوظة؟',
    openOriginal: 'فتح الملف',
    px: 'بكسل',
    mm: 'مم',
  },
  sync: {
    title: 'ما جرى دون اتصال',
    sub: 'ما قررته القواعد أثناء عمل حاسوب دون اتصال. لا شيء هنا يحتاج حلاً: السجل كما تركته القاعدة. ألقِ نظرة، تصرّف إن شئت، ثم أقرّ.',
    attention: 'يحتاج نظرة',
    forTheRecord: 'للسجل',
    nothingOpen: 'لا شيء بانتظار نظرة.',
    nothingYet: 'لا نشاط دون اتصال في آخر 30 يوماً.',
    showAll: 'أظهر المُقرّ أيضاً',
    acknowledge: 'إقرار',
    acknowledged: 'تم الإقرار',
    by: 'بواسطة {name}',
    fromDevice: 'من {device}',
    openPatient: 'فتح المريض',
    openCalendar: 'فتح المواعيد',
    openFinance: 'فتح المالية',
    openInventory: 'فتح المستودع',
    sentFromHere: 'أُرسل من هذا الحاسوب',
    sentFromHereSub: 'كل تغيير أُجري هنا وما فعله الخادم به.',
    couldNotSend: 'تعذّر تطبيقه',
    couldNotSendSub: 'رفضها الخادم؛ السبب مبيّن. تبقى هنا لمالك العيادة.',
    state: { pending: 'بانتظار الإرسال', in_flight: 'جارٍ الإرسال', acknowledged: 'طُبّق', conflict: 'قُرّر بقاعدة', recovery: 'مرفوض' },
    noLog: 'لم يُرسل شيء من هذا الحاسوب بعد.',
    rules: 'القواعد',
    rulesText: [
      'حقلاً حقلاً، يفوز التغيير الأحدث؛ ويبقى التغيير الذي سبقه في السجل.',
      'الموعد المحجوز دون اتصال في وقت شُغل في الأثناء يُحفظ ويُعرض بالأحمر حتى ينقله الاستقبال.',
      'العمل المفوتر مرتين يحتفظ بسطر فاتورته الأولى؛ ويُسقط السطر المكرر.',
      'لا تُسقَط دفعة أبداً: تسدّد الفواتير المفتوحة الأقدم أولاً، وما زاد عنها رصيد في الحساب.',
      'قد ينزل المخزون تحت الصفر؛ يقول ذلك ويطلب جرداً.',
      'المرجع المؤقت للفاتورة يصبح رقماً رسمياً عند وصوله إلى الخادم.',
    ],
    kinds: {
      booking_overlap: 'موعد يتداخل مع آخر',
      duplicate_booking: 'الحجز نفسه مرتين — حُفظ واحد',
      possible_duplicate_payment: 'دفعة قد تكون مكررة',
      payment_applied: 'طُبّقت الدفعة',
      invoice_numbered: 'رُقّمت الفاتورة',
      line_dropped: 'أُسقط سطر فاتورة (مفوتر سابقاً)',
      invoice_dropped: 'لم تُنشأ الفاتورة (مفوترة سابقاً)',
      possible_duplicate_treatment: 'الإجراء نفسه سُجّل مرتين',
      stock_negative: 'المخزون تحت الصفر',
      superseded_edit: 'تعديل أقدم حُفظ في السجل',
      superseded_status: 'الطلب كان قد تقدّم',
      not_applied: 'لم يُطبّق',
    },
    detail: {
      booking_overlap: 'كان الوقت {at} مشغولاً عندما وصل هذا الحجز. حُفظ ويُعرض بالأحمر؛ انقله أو اتركه.',
      duplicate_booking: 'حُجز المريض نفسه مع الطبيب نفسه في الوقت نفسه من حاسوبين. حُفظ حجز واحد.',
      possible_duplicate_payment: 'سُجّل {amount} مجدداً لهذا المريض خلال يومين من دفعة أخرى بالمبلغ نفسه. حُفظت الاثنتان؛ اعكس إحداهما إن كانت مكررة.',
      payment_applied: 'استُلم {amount}: طُبّق {applied} على الفواتير{credit}.',
      payment_credit: '، وبقي {credit} رصيداً في الحساب',
      invoice_numbered: 'أصبح {ref} هو {number}.',
      line_dropped: '«{description}» كان على فاتورة أخرى، فتُرك خارج هذه.',
      invoice_dropped: 'كل سطور {ref} كانت مفوترة في مكان آخر، فلم تُنشأ فاتورة.',
      possible_duplicate_treatment: '{name} على {tooth} كان مسجلاً في ذلك اليوم. حُفظ الاثنان؛ احذف أحدهما إن كان مكرراً.',
      stock_negative: '{name} عند {onHand}: السجلات تقول أقل من لا شيء. اجرده وسجّل تسوية.',
      superseded_edit: 'تغيّر {fields} على الخادم بعد هذا التعديل. حُفظت القيمة الأحدث؛ وهذه في السجل.',
      superseded_status: 'كان الطلب عند «{kept}»؛ لم يُطبّق «{wanted}».',
      not_applied: 'رفضه الخادم: {message}',
    },
    flags: {
      superseded: 'أحدث على الخادم',
      overlap: 'تداخل',
      duplicate: 'مكرر',
      line_dropped: 'سطر أُسقط',
      invoice_dropped: 'لم تُنشأ',
      not_applied: 'لم يُطبّق',
      numbered: 'رُقّمت',
      credit: 'رصيد',
      stock_negative: 'مخزون تحت الصفر',
      possible_duplicate: 'قد يكون مكرراً',
    },
    entities: {
      patient: 'مريض', medical_history: 'التاريخ الطبي', appointment: 'موعد', treatment: 'إجراء', tooth_observation: 'ملاحظة مخطط', surface_finding: 'ملاحظة سطح', zone_observation: 'مخطط الوجه والجسم', clinical_note: 'ملاحظة سريرية', attachment: 'صورة', invoice: 'فاتورة', payment: 'دفعة', stock_movement: 'حركة مخزون', lab_order: 'طلب مخبر',
    },
    actions: { create: 'أُضيف', update: 'عُدّل', delete: 'حُذف', append: 'سُجّل', post: 'رُحّل' },
    temporaryRef: 'مرجع مؤقت حتى المزامنة',
  },
  capture: {
    title: 'التقاط من الحساس',
    sub: 'عبر تعريف الحساس نفسه على هذا الحاسوب. نافذة التعريف تتحكم بالتعريض؛ والصورة تعود إلى هنا.',
    source: 'الحساس',
    noSources: 'لم يُعثر على تعريف حساس على هذا الحاسوب. ثبّت تعريف TWAIN الخاص بالحساس، أو استورد من مجلد تصديره أدناه.',
    simulatorNote: 'الحساس المحاكي ينتج صورة اصطناعية موسومة لتجربة سير العمل. ليست صورة شعاعية حقيقية أبداً.',
    showUi: 'إظهار نافذة الالتقاط الخاصة بالتعريف',
    capture: 'التقاط',
    capturing: 'بانتظار الحساس…',
    captured: 'التُقطت {n} صورة',
    failed: 'لم ينتج الالتقاط صورة.',
    errors: {
      bridge_missing: 'جسر الحساس مفقود من هذا التثبيت.',
      source_not_found: 'هذا الحساس لم يعد مدرجاً. حدّث القائمة.',
      opends_failed: 'تعذّر فتح التعريف. هل الحساس موصول وبرنامجه مغلق؟',
      enable_failed: 'رفض التعريف بدء الالتقاط.',
      timeout: 'لم تصل صورة قبل انتهاء الوقت.',
      cancelled: 'أُلغي الالتقاط في نافذة التعريف.',
      no_image: 'أشار التعريف إلى صورة لكن تعذّرت قراءتها.',
      no_dsm: 'لا مدير TWAIN مثبّت لهذا التعريف.',
    },
    preview: 'الصورة الملتقطة',
    pixelSize: 'حجم البكسل من التعريف: {um} ميكرومتر — يُضبط مقياس العارض منه.',
    noPixelSize: 'لم يُبلّغ التعريف عن حجم البكسل؛ اضبط المقياس في العارض من طول معروف.',
    saveAll: 'حفظ في ملف المريض',
    saving: 'جارٍ الحفظ…',
    saved: 'حُفظ',
    discard: 'تجاهل',
    kind: 'النوع',
    tooth: 'السن',
    folderTitle: 'استيراد من مجلد برنامج الحساس',
    folderSub: 'عندما يحفظ برنامج الحساس صوره في مجلد، تظهر الملفات الجديدة هنا.',
    folderPath: 'المجلد',
    folderMissing: 'هذا المجلد غير موجود على هذا الحاسوب.',
    folderNone: 'لم يُحدد مجلد بعد.',
    folderEmpty: 'لا صور جديدة في المجلد.',
    folderNew: 'جديد',
    import: 'استيراد',
    refresh: 'تحديث',
    settings: 'إعدادات الحساس',
    transfer: 'طريقة النقل',
    transferMemory: 'عينات خام (تحافظ على 16 بت)',
    transferNative: 'صورة ويندوز النقطية (الأكثر توافقاً)',
    selfTest: 'اختبار الجسر',
    selfTestResult: 'الجسر {arch}: {result}',
    save: 'حفظ الإعدادات',
  },
  imaging: {
    title: 'الصور',
    upload: 'رفع صورة',
    uploading: 'جارٍ الرفع…',
    file: 'الملف',
    acquiredOn: 'تاريخ التصوير',
    acquiredOnHint: 'متى التُقطت الصورة، وليس دائماً اليوم.',
    tooLarge: 'أكبر من الحد المسموح {limit}',
    wholeMouthNote: 'هذا النوع يغطي الفم كاملاً، لذلك لا يُربط بسنٍّ واحد.',
    addImage: 'إضافة صورة',
    noneYet: 'لا توجد صور في ملف هذا المريض بعد.',
    kinds: {
      panoramic: 'بانوراما (OPG)',
      cbct: 'أشعة مقطعية مخروطية',
      periapical: 'ذروية',
      bitewing: 'عضّية',
      cephalometric: 'قياس رأسي',
      intraoral_photo: 'صورة داخل الفم',
      extraoral_photo: 'صورة خارج الفم',
      document: 'مستند',
      consent: 'استمارة موافقة',
      report: 'تقرير',
      other: 'أخرى',
    },

    library: 'صور المريض',
    noImages: 'لا توجد صور لهذا المريض.',
    kind: 'النوع',
    source: 'المصدر',
    linkedTeeth: 'الأسنان المرتبطة',
    dicomNote: 'تُحفظ دراسات DICOM مع مصدرها. عارض CBCT غير متوفر بعد.',
  },
  sources: {
    title: 'مصادر المرضى',
    sub: 'من أين يأتي مرضاك. يختار الاستقبال مصدرًا عند تسجيل المريض، والتقارير تحصيها.',
    label: 'المصدر',
    none: 'غير مسجَّل',
    referredBy: 'محال من طبيب',
    referredByOption: 'محال من {name}',
    dentistsGroup: 'أطباء العيادة',
    detail: 'تفاصيل المصدر',
    detailHint: 'اختياري: من أرسله تحديدًا',
    add: 'إضافة مصدر',
    nameEn: 'الاسم (بالإنجليزية)',
    nameAr: 'الاسم (بالعربية)',
    retire: 'إيقاف',
    restore: 'إعادة الاستخدام',
    remove: 'حذف',
    retired: 'موقوف',
    patientsCount: '{n} مريض',
    retiredInstead: 'لدى بعض المرضى هذا المصدر، لذلك أُوقف بدل حذفه.',
    empty: 'لا توجد مصادر بعد.',
    save: 'حفظ',
    cancel: 'إلغاء',
  },
  journey: {
    tab: 'الزيارات',
    upcoming: 'قادم',
    nextPlan: 'المخطط للمرة القادمة',
    plan: 'خطة المعالجة (لم تُنفَّذ بعد)',
    none: 'لا زيارات بعد.',
    done: 'ما أُنجز في هذه الزيارة',
    nothingDone: 'لا أعمال مسجلة لهذه الزيارة.',
    addNote: 'إضافة ملاحظة الزيارة',
    whatDone: 'ما الذي أُنجز',
    whatDoneHint: 'المعالجة المنجزة، والملاحظات، وكل ما يجب أن يعرفه الطبيب التالي',
    next: 'خطة الزيارة القادمة',
    nextHint: 'اختياري',
    share: 'مشاركة مع أطباء العيادة',
    shareHint: 'تظهر الملاحظة المشتركة لبقية الأطباء دون اسمك. ويرى مديرو العيادة كل الملاحظات.',
    private: 'خاصة',
    shared: 'مشتركة',
    privateHint: 'لا يرى هذه الملاحظة إلا أنت ومديرو العيادة',
    aDentist: 'أحد أطباء العيادة',
    you: 'أنت',
    makeShared: 'مشاركة',
    makePrivate: 'جعلها خاصة',
    save: 'حفظ الملاحظة',
    saving: 'جارٍ الحفظ…',
    cancel: 'إلغاء',
    with: 'مع {name}',
    followedUp: 'تمت المتابعة: {s}',
    notesHidden: 'الملاحظات للفريق السريري.',
    status: {
      completed: 'حضر',
      checked_in: 'سُجّل حضوره',
      in_treatment: 'قيد المعالجة',
      no_show: 'لم يحضر',
      cancelled: 'ملغى',
      scheduled: 'مجدول',
      confirmed: 'مؤكد',
      visit: 'زيارة',
      work: 'أعمال مسجلة',
      note: 'ملاحظة',
    },
  },
  missed: {
    title: 'مواعيد فائتة تحتاج متابعة',
    sub: 'مرضى لم يحضروا. اتصل بهم، أو احجز لهم من جديد، أو أغلق الحالة.',
    none: 'لا مواعيد فائتة تحتاج متابعة.',
    open: 'للمتابعة',
    all: 'الكل',
    missedOn: 'فاته موعد {date}',
    nextBooked: 'حُجز من جديد في {date}',
    note: 'ملاحظة (اختياري)',
    reopen: 'إعادة فتح',
    by: 'بواسطة {name}',
    count: '{n} للمتابعة',
    openList: 'فتح القائمة',
    action: { contacted: 'تم التواصل', rebooked: 'أُعيد الحجز', closed: 'إغلاق' },
    status: { contacted: 'تم التواصل', rebooked: 'أُعيد الحجز', closed: 'مغلق' },
  },
  insights: {
    title: 'التقارير',
    sub: 'ما جرى في العيادة خلال فترة.',
    ownScope: 'أرقامك أنت: المرضى الذين عاينتهم أو أحلتهم، وإجراءاتك، ومواعيدك الفائتة.',
    tabs: { overview: 'نظرة عامة', patients: 'مرضى جدد', dentists: 'الأطباء', procedures: 'الإجراءات', missed: 'المواعيد الفائتة' },
    newPatients: 'مرضى جدد',
    patientsSeen: 'مرضى عوينوا',
    completed: 'مواعيد حضرها المرضى',
    noShows: 'مواعيد فائتة',
    noShowRate: 'نسبة عدم الحضور',
    rateHint: 'من المواعيد المستحقة (حضر أو لم يحضر)، نسبة التي لم يحضرها المريض.',
    cancelled: 'ملغاة',
    procedures: 'إجراءات منجزة',
    bySource: 'حسب المصدر',
    byReferrer: 'محالون من الأطباء',
    notRecorded: 'غير مسجَّل',
    list: 'المرضى',
    registered: 'تاريخ التسجيل',
    dentist: 'الطبيب',
    referred: 'مرضى أحالهم',
    value: 'قيمة الإجراءات',
    count: 'العدد',
    patientsCol: 'المرضى',
    procedure: 'الإجراء',
    exportCsv: 'تنزيل (CSV)',
    empty: 'لا شيء في هذه الفترة.',
    allDentists: 'كل الأطباء',
    financeLink: 'الأموال (الفواتير والمدفوعات والمصروفات) في المالية، ضمن التقارير.',
    followup: 'المتابعة',
    notFollowed: 'لم تتم متابعته',
  },
  dictation: {
    start: 'إملاء صوتي',
    stop: 'إيقاف',
    listening: 'أستمع… تحدّث ثم اضغط إيقاف',
    transcribing: 'جارٍ تحويل الكلام إلى نص…',
    loadingModel: 'تجهيز التعرّف على الكلام على هذا الحاسوب (أول مرة فقط: نحو 550 ميغابايت، أو 240 دون بطاقة رسومية)…',
    unsupported: 'الإملاء الصوتي غير متاح في هذا المتصفح.',
    denied: 'اسمح باستخدام الميكروفون للإملاء.',
    failed: 'تعذّر تحويل التسجيل إلى نص. حاول مجددًا وأنت أقرب إلى الميكروفون.',
    settingTitle: 'الملاحظات الصوتية',
    settingSub: 'يستطيع الأطباء إملاء الملاحظات بدل كتابتها.',
    device: 'على هذا الحاسوب (خاص)',
    deviceHint: 'لا يغادر الصوت الحاسوب. أول استخدام ينزّل نموذج الكلام مرة واحدة (أدق نموذج يستطيع الحاسوب تشغيله). يعمل في تطبيق ويندوز ودون إنترنت.',
    browser: 'خدمة الكلام في المتصفح (أسرع)',
    browserHint: 'دقيقة جدًا وفورية، لكن الصوت يُرسل إلى Google أو Microsoft لتحويله إلى نص. غير متاحة في تطبيق ويندوز، الذي يستخدم الخيار الخاص دائمًا.',
    off: 'متوقف',
    save: 'حفظ',
    saved: 'تم الحفظ',
  },
  patientEdit: {
    edit: 'تعديل البيانات',
    title: 'تعديل بيانات المريض',
  },
};

export const MODULE_DICTIONARIES = { en, ar };
